import axios from "axios";
import { hardwareBridge } from '../../../services/hardwareBridge';
import { Storage } from "@/services/Storage";
import { Device } from "@/services/Device";
import store from "@/store";
import router from "@/router";
import { generateUUID } from '@/utils/uuid';
import outbox from '@/services/outbox';
import voidOutbox from '@/services/voidOutbox';
import connectivityMonitor from '@/services/connectivityMonitor';
import Swal from 'sweetalert2';
import OrdersService from '@/services/OrdersService';
import paidTablesOutbox from '@/services/paidTablesOutbox';
import vatCalc from '@/services/vatCalc';
import tableServerClient from '@/services/tableServerClient';
// docs/FRONTEND.md § Sales-write user field — #26328 / Sentry 1V+1E
import { requireSalesWriteUser } from '@/services/salesWriteUser';
// docs/FRONTEND.md — sale line Qty contract; qty-reset-on-save fix
// Loss vectors #3 (openTable hydrate) and #4 (Unit merge key toFixed drift)
import {
    normalizeSaleLines,
    saleLineUnitsMatch,
} from '@/utils/saleLineQty';

const getStoreDB = async () => {
    const cached = store.getters.getDefaultStore;
    if (cached && cached.StoreDB) return cached.StoreDB;
    const s = await Storage.getDefaultStore();
    return s?.StoreDB;
};

const getLineVatBase = (line) => {
    const qty = Number(line.Qty) || 0;
    const unit = Number(line.Unit) || 0;
    let base = (unit * qty) - Number(line.Discount || 0);
    if (line.instructions && line.instructions.length > 0) {
        const instructionTotal = line.instructions
            .map(i => Number(i.Price) * Number(i.count || 1))
            .reduce((acc, n) => Number(acc) + Number(n), 0);
        base += Number(instructionTotal);
    }
    return Number(base) || 0;
};

const getLineTaxMeta = (line) => {
    const stockItem = store.getters.getByPlu ? store.getters.getByPlu(line.Plu) : null;
    let taxLabel = line.TaxLabel || line.taxLabel || '';
    if (!taxLabel && stockItem && stockItem.TaxLabels) {
        const taxCategory = store.getters.getZraCategoryByLabel
            ? store.getters.getZraCategoryByLabel(stockItem.TaxLabels)
            : null;
        taxLabel = taxCategory ? taxCategory.TaxLabel : '';
    }
    if (!taxLabel && stockItem) {
        taxLabel = stockItem.TaxLabel || stockItem.taxLabel || stockItem.Tax || '';
    }
    const recommendedRetail =
        Number(line.RecommendedRetail ?? line.recommendedRetail ?? line.mtv) ||
        Number(stockItem?.RecommendedRetail ?? stockItem?.recommendedRetail ?? stockItem?.StockSetting?.mtv) ||
        0;
    return { taxLabel, recommendedRetail };
};

const updateLineVat = async (line, storeDB) => {
    if (!line) return;
    const db = storeDB || await getStoreDB();
    const { taxLabel, recommendedRetail } = getLineTaxMeta(line);
    line.TaxLabel = taxLabel || line.TaxLabel || '';
    line.RecommendedRetail = recommendedRetail;
    const base = getLineVatBase(line);
    const vat = await vatCalc.getVatTotal(base, true, (taxLabel || 'A'), recommendedRetail, db);
    line.vat = Number(vat || 0);
};

let getNow = function () {
    let date = new Date();
    let d = date.toISOString().split('T')[0]
    let t = date.toISOString().split('T')[1].split('.')[0];
    return d + ` ` + t;
}

// Offline-only: generate a 10-digit numeric sale number (timestamp-based)
const generateOfflineSaleNumber = () => {
    const ts = Date.now().toString();
    return Number(ts.slice(-10));
};
const isEmpty = function (obj) {
    if (!obj) {
        return true;
    }
    return Object.keys(obj).length === 0;
}
let validateSecurity = function (security) {
    return store.dispatch(`validateSecurityPermission`, security);
}

const resolveHostname = async (settings = null) => {
    try {
        if (!settings) settings = await Storage.getDeviceSettings();
    } catch (e) {
        settings = null;
    }

    if (settings?.PC) return settings.PC;

    try {
        if (window?.hardwareIPC || window?.electronBridge) {
            const data = await hardwareBridge.getPcData();
            const hostname = data?.hostname || data?.hostName || data?.HostName || (typeof data === 'string' ? data : '');
            if (hostname) return hostname;
            const name = await hardwareBridge.getPcName();
            if (typeof name === 'string' && name) return name;
            if (name?.hostname) return name.hostname;
        }
    } catch (e) {
        // ignore and fallback
    }

    try {
        return await Device.getPCName(!!window.androidMode);
    } catch (e) {
        return 'UNKNOWN';
    }
}

const isMainPcDevice = async () => {
    try {
        return await tableServerClient.checkIsMainPC();
    } catch (e) {
        return false;
    }
};

const ensureMainPcReachableForWrite = async () => {
    const isMain = await isMainPcDevice();
    if (isMain) {
        // Main PC should not perform an additional connectivity probe here.
        // Invoice flow already runs the check-threads probe before posting.
        return true;
    }

    const reachable = await tableServerClient.testConnection();
    if (!reachable) {
        const issue = tableServerClient.getLastConnectionIssue ? tableServerClient.getLastConnectionIssue() : null;
        const reason = issue?.reason || 'Main device is unreachable.';
        const host = issue?.host ? `<br><b>Main PC:</b> ${issue.host}` : '';
        const code = issue?.code ? `<br><b>Error Code:</b> ${issue.code}` : '';
        await Swal.fire({
            icon: 'error',
            title: 'Cannot Process Sale',
            html: `Main device is unreachable, so sales are blocked.<br><br>${reason}${host}${code}<br><br>Please reconnect to Main PC and try again.`,
            confirmButtonText: 'OK'
        });
        throw new Error(`Main device is unreachable. ${reason}`);
    }
    return true;
};

const DUPLICATE_SALE_MESSAGE_REGEX = /sale\s*:\s*#?\s*(\d+)\s+has\s+already\s+been\s+invoice/i;
const MAX_DUPLICATE_SALE_RETRIES = 3;

const toInvoiceResponseText = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return value.map(v => String(v ?? '')).join('');
    if (typeof value !== 'object') return '';

    const keys = Object.keys(value);
    if (keys.length > 0 && keys.every(k => /^\d+$/.test(k))) {
        return keys
            .sort((a, b) => Number(a) - Number(b))
            .map(k => String(value[k] ?? ''))
            .join('');
    }

    if (typeof value.message === 'string') return value.message;
    if (typeof value.error === 'string') return value.error;
    if (typeof value.response === 'string') return value.response;
    return '';
};

const getDuplicateSaleNumberFromResponse = (data) => {
    const text = toInvoiceResponseText(data).trim();
    if (!text) return 0;
    const match = text.match(DUPLICATE_SALE_MESSAGE_REGEX);
    if (!match) return 0;
    const saleNumber = Number(match[1]);
    return Number.isFinite(saleNumber) && saleNumber > 0 ? Math.trunc(saleNumber) : 0;
};

const getPayloadSaleNumber = (payload) => {
    const saleNumber = Number(payload?.saleNumber ?? payload?.SaleNum ?? 0);
    return Number.isFinite(saleNumber) && saleNumber > 0 ? Math.trunc(saleNumber) : 0;
};

const getReservedSaleNumberFromResponse = (data) => {
    const source = String(data?.source || '').toLowerCase();
    if (source === 'local') return 0;
    const raw = data?.response ?? data?.saleNum ?? data?.saleNumber ?? data;
    const value = Number(raw);
    return Number.isFinite(value) && value > 0 ? Math.trunc(value) : 0;
};

const fetchFreshSaleNumberThroughMainPc = async (storeDB) => {
    try {
        const data = await tableServerClient.proxyGetSaleNum(storeDB);
        const fromProxy = getReservedSaleNumberFromResponse(data);
        if (fromProxy > 0) return fromProxy;
    } catch (e) {
        // continue to direct backend fallback
    }
    try {
        const { data } = await axios.get(`${serverApi}invoice/${storeDB}/salenum`, { timeout: 10000 });
        return getReservedSaleNumberFromResponse(data);
    } catch (e) {
        return 0;
    }
};

// On "sale already invoiced" retry: bump header saleNumber AND every item's
// salenum/SaleNum so backend line lookup matches the new header (else 406).
// docs/FRONTEND.md — mobile invoice retry contract
const withUpdatedSaleNumber = (payload, saleNumber) => {
    if (!payload || !saleNumber) return payload;
    const out = {
        ...payload,
        saleNumber,
        SaleNum: saleNumber,
        localId: generateUUID()
    };
    // CRITICAL: stamp every item's salenum to match the new header
    if (Array.isArray(out.items)) {
        out.items = out.items.map(item => ({
            ...item,
            salenum: saleNumber,
            SaleNum: saleNumber,
        }));
    }
    return out;
};

const postInvoiceThroughMainPc = async (storeDB, payload) => {
    // Always use Main-PC table proxy so sale number assignment is centralized in one place.
    let requestPayload = { ...payload };
    for (let attempt = 0; attempt <= MAX_DUPLICATE_SALE_RETRIES; attempt++) {
        try {
            const data = await tableServerClient.proxyInvoiceMobile(requestPayload, storeDB);
            const duplicateSaleNumber = getDuplicateSaleNumberFromResponse(data);

            if (!duplicateSaleNumber) {
                return { data, status: 200 };
            }

            if (attempt >= MAX_DUPLICATE_SALE_RETRIES) {
                const err = new Error(`Sale number collision persisted after ${MAX_DUPLICATE_SALE_RETRIES + 1} attempts.`);
                err.response = { status: 409, data };
                throw err;
            }

            let nextSaleNumber = 0;
            if (attempt >= 1) {
                nextSaleNumber = await fetchFreshSaleNumberThroughMainPc(storeDB);
            }
            if (!nextSaleNumber) {
                const payloadSaleNumber = getPayloadSaleNumber(requestPayload);
                nextSaleNumber = Math.max(duplicateSaleNumber + 1, payloadSaleNumber + 1, 1);
            }
            console.warn(`[Sales] Sale #${duplicateSaleNumber} already invoiced. Retrying with sale #${nextSaleNumber}.`);
            requestPayload = withUpdatedSaleNumber(requestPayload, nextSaleNumber);
        } catch (error) {
            const duplicateSaleNumber = getDuplicateSaleNumberFromResponse(error?.response?.data);
            if (!duplicateSaleNumber) throw error;

            if (attempt >= MAX_DUPLICATE_SALE_RETRIES) {
                throw error;
            }

            let nextSaleNumber = 0;
            if (attempt >= 1) {
                nextSaleNumber = await fetchFreshSaleNumberThroughMainPc(storeDB);
            }
            if (!nextSaleNumber) {
                const payloadSaleNumber = getPayloadSaleNumber(requestPayload);
                nextSaleNumber = Math.max(duplicateSaleNumber + 1, payloadSaleNumber + 1, 1);
            }
            console.warn(`[Sales] Sale #${duplicateSaleNumber} already invoiced (error response). Retrying with sale #${nextSaleNumber}.`);
            requestPayload = withUpdatedSaleNumber(requestPayload, nextSaleNumber);
        }
    }
    throw new Error('Failed to post invoice after duplicate sale retries.');
};

const markOpenTableThroughMainPc = async (storeDB, saleNum) => {
    const isMain = await isMainPcDevice();
    if (isMain) {
        return axios.get(serverApi + `tables/${storeDB}/openTable/${saleNum}`);
    }
    const data = await tableServerClient.proxyOpenTable(storeDB, saleNum);
    return { data, status: 200 };
};

const sendToRoomThroughMainPc = async (storeDB, payload) => {
    const isMain = await isMainPcDevice();
    if (isMain) {
        return axios.post(`${serverApi}tables/${storeDB}/sendToRoom`, payload, { timeout: 10000 });
    }
    const data = await tableServerClient.proxySendToRoom(payload, storeDB);
    return { data, status: 200 };
};

const SALE_LINES_PERSIST_DEBOUNCE_MS = 180;
let persistSaleLinesTimer = null;
let lastPersistedSaleLinesPayload = null;

const writeSaleLinesToLocalStorage = (serializedPayload, hasLines) => {
    if (serializedPayload === lastPersistedSaleLinesPayload) return;
    try {
        if (hasLines) {
            localStorage.setItem('currentSaleLines', serializedPayload);
        } else {
            localStorage.removeItem('currentSaleLines');
        }
        lastPersistedSaleLinesPayload = serializedPayload;
    } catch (e) {
        console.warn('[sales] Failed to persist sale lines to localStorage:', e);
    }
};

const persistSaleLinesToLocalStorage = (lines = [], options = {}) => {
    const hasLines = Array.isArray(lines) && lines.length > 0;
    let serializedPayload = '';
    if (hasLines) {
        try {
            serializedPayload = JSON.stringify(lines);
        } catch (e) {
            console.warn('[sales] Failed to serialize sale lines for localStorage:', e);
            return;
        }
    }

    const immediate = Boolean(options?.immediate);
    if (persistSaleLinesTimer) {
        clearTimeout(persistSaleLinesTimer);
        persistSaleLinesTimer = null;
    }

    if (immediate) {
        writeSaleLinesToLocalStorage(serializedPayload, hasLines);
        return;
    }

    persistSaleLinesTimer = setTimeout(() => {
        writeSaleLinesToLocalStorage(serializedPayload, hasLines);
        persistSaleLinesTimer = null;
    }, SALE_LINES_PERSIST_DEBOUNCE_MS);
};

const SALE_HEAVY_RECALC_DEBOUNCE_MS = 45;
let saleHeavyRecalcTimer = null;
const scheduleSaleHeavyRecalc = () => {
    if (saleHeavyRecalcTimer) {
        clearTimeout(saleHeavyRecalcTimer);
        saleHeavyRecalcTimer = null;
    }
    saleHeavyRecalcTimer = setTimeout(() => {
        saleHeavyRecalcTimer = null;
        try {
            store.commit('recalcSpecials');
        } catch (e) {
            console.warn('[sales] Failed to run deferred sale recalculation:', e);
        }
    }, SALE_HEAVY_RECALC_DEBOUNCE_MS);
};

const bumpTableNextSaleNum = (storeDb, saleNumber) => {
    try {
        const n = Number(saleNumber);
        if (!Number.isFinite(n) || n <= 0) return;
        const normalized = Math.trunc(n);
        const currentNext = Number(localStorage.getItem('tableNextSaleNum') || 0);
        const mergedNext = Math.max(
            normalized + 1,
            Number.isFinite(currentNext) && currentNext > 0 ? Math.trunc(currentNext) : 0
        );
        localStorage.setItem('tableNextSaleNum', String(mergedNext));
        if (storeDb) {
            const reservedKey = `tableReservedSaleNum_${storeDb}`;
            const reserved = Number(localStorage.getItem(reservedKey) || 0);
            if (Number.isFinite(reserved) && reserved > 0 && reserved <= normalized) {
                localStorage.removeItem(reservedKey);
            }
        }
    } catch (_) { }
};

const getInvoiceByNumberThroughMainPc = async (storeDB, invoice) => {
    const isMain = await isMainPcDevice();
    if (isMain) {
        return axios.get(serverApi + `invoice/${storeDB}/getInvNum`, { params: { invoice } });
    }
    const data = await tableServerClient.proxyGetInvoiceByNumber(storeDB, invoice);
    return { data, status: 200 };
};

const logVoidThroughMainPc = async (storeDB, payload) => {
    const isMain = await isMainPcDevice();
    if (isMain) {
        return axios.post(serverApi + `supervisorOverrides/${storeDB}/logItemVoid`, payload, { timeout: 10000 });
    }
    const data = await tableServerClient.proxyLogItemVoid(payload, storeDB);
    return { data, status: 200 };
};

const broadcastSaleUpdate = (state) => {
    persistSaleLinesToLocalStorage(state.currentSaleLines || []);

    const lines = state.currentSaleLines || [];

    // If no lines, clear the customer display and return to IDLE
    if (lines.length === 0) {
        if (window.screenAPI && window.screenAPI.clearDisplay) {
            window.screenAPI.clearDisplay().catch(e => console.error('[CustomerDisplay] Clear failed:', e));
        }
        return;
    }

    if (!window.screenAPI || !window.screenAPI.sendSaleData) return;

    try {
        // Debug logging
        // console.log('[CustomerDisplay] Broadcasting update. Lines:', lines.length);

        // Calculate total similar to getSaleTotal
        const total = lines.map(line => {
            let t = (Number(line.Qty) * Number(line.Unit)) + (Number(line.Qty) * Number(line.tourismLevy || 0)) - Number(line.Discount || 0);
            if (line.instructions && line.instructions.length > 0) {
                let instTotal = line.instructions.reduce((acc, i) => Number(acc) + (Number(i.Price) * Number(i.count)), 0);
                t += Number(instTotal);
            }
            return Number(t);
        }).reduce((sum, val) => Number(sum) + Number(val), 0);

        const vat = lines.map(l => Number(l.vat || 0)).reduce((sum, v) => Number(sum) + Number(v), 0);
        const subtotal = total - vat;

        // Get currency from store
        const currency = store.getters.getBaseCurrency || { Symbol: 'K', Name: 'Kwacha' };

        const payload = {
            items: lines.map(l => ({
                plu: l.Plu || '',
                name: l.Description || 'Unknown Item',
                quantity: Number(l.Qty) || 0,
                unitPrice: Number(l.Unit) || 0,
                lineTotal: Number(l.Line) || 0,
                discount: Number(l.Discount) || 0,
                taxLabel: l.TaxLabel || ''
            })),
            totals: {
                subtotal: Number(subtotal) || 0,
                tax: Number(vat) || 0,
                grandTotal: Number(total) || 0
            },
            currency: currency,
            storeName: store.getters.getDefaultStore ? store.getters.getDefaultStore.StoreName : 'Lytning POS'
        };

        // console.log('[CustomerDisplay] Payload:', JSON.stringify(payload));

        window.screenAPI.sendSaleData(payload).catch(e => console.error('[CustomerDisplay] Update failed:', e));
    } catch (e) {
        console.error('[CustomerDisplay] Error preparing update:', e);
    }
};

const getLedSettings = () => {
    return store.getters.getDeviceSettings || store.state.device_settings || store.state.settings || {};
};

// Broadcast sale updates to LED pole display
// Note: This is synchronous because it's called from mutations
// LED settings are read from the store's settings object directly
const broadcastLedUpdate = (state) => {
    if (!window.ledDisplay || !window.ledDisplay.showWithMode) {
        return;
    }

    try {
        const ledSettings = getLedSettings();
        const port = ledSettings.ledPort || 'COM2';
        const baudRate = ledSettings.ledBaudRate || 2400;

        const lines = state.currentSaleLines || [];

        const total = lines.reduce((sum, line) => {
            let lineTotal = (Number(line.Qty) * Number(line.Unit)) + (Number(line.Qty) * Number(line.tourismLevy || 0)) - Number(line.Discount || 0);
            if (line.instructions && line.instructions.length > 0) {
                lineTotal += line.instructions.reduce((acc, i) => Number(acc) + (Number(i.Price) * Number(i.count)), 0);
            }
            return Number(sum) + Number(lineTotal);
        }, 0);

        if (lines.length === 0) {
            window.ledDisplay.clear(port, baudRate).catch(e => console.error('[LED] Clear failed:', e));
            return;
        }

        window.ledDisplay.showWithMode('total', total.toFixed(2), port, baudRate)
            .catch(e => console.error('[LED] Display total failed:', e));
    } catch (e) {
        console.error('[LED] Error updating display:', e);
    }
};

/**
 * Build a void receipt as ESC/POS commands and return a base64 string.
 * Extracted from printVoidOffline so it can be reused for item-printer void slips.
 * @param {Object} data - { user, pc, reason, supervisor, items: [{description, qty, value}] }
 * @param {Array}  items - items to include (may be a subset of data.items)
 * @param {Object} store - store info from Storage.getDefaultStore()
 * @param {Object} settings - device settings from Storage.getDeviceSettings()
 * @returns {string} base64 encoded ESC/POS byte array
 */
function buildVoidReceiptEscPos(data, items, store, settings) {
    const ESC = 0x1B;
    const GS = 0x1D;

    const receiptType = settings?.ReceiptType || 'Normal';
    const is55mm = receiptType === '55ml';
    const lineWidth = is55mm ? 32 : 48;

    const commands = [];

    // Initialize printer
    commands.push(ESC, 0x40);

    // Store name - Bold, Center, Large
    commands.push(ESC, 0x61, 0x01); // Center align
    commands.push(ESC, 0x45, 0x01); // Bold on
    commands.push(GS, 0x21, 0x11); // Double width & height
    const storeName = store?.StoreName || 'Store';
    commands.push(...Array.from(storeName).map(c => c.charCodeAt(0)));
    commands.push(0x0A);
    commands.push(GS, 0x21, 0x00); // Reset size
    commands.push(ESC, 0x45, 0x00); // Bold off

    // Location
    const location = store?.Location || '';
    if (location) {
        commands.push(...Array.from(location).map(c => c.charCodeAt(0)));
        commands.push(0x0A);
    }

    // Store details
    const storeDetails = store?.StoreDetails || '';
    if (storeDetails) {
        commands.push(...Array.from(storeDetails).map(c => c.charCodeAt(0)));
        commands.push(0x0A);
    }

    // TPIN
    let tpin = store?.TPIN || '';
    if (!tpin) {
        const addr3 = store?.Addr3 || '';
        const match = String(addr3).match(/\b\d{6,}\b/);
        if (match) tpin = match[0];
    }
    if (tpin) {
        commands.push(...Array.from(`TPIN : ${tpin}`).map(c => c.charCodeAt(0)));
        commands.push(0x0A);
    }

    commands.push(0x0A); // Empty line

    // "Void Receipt" header - Bold, Center
    commands.push(ESC, 0x45, 0x01); // Bold on
    commands.push(...Array.from('Void Receipt').map(c => c.charCodeAt(0)));
    commands.push(0x0A);
    commands.push(ESC, 0x45, 0x00); // Bold off

    // Separator
    commands.push(ESC, 0x61, 0x00); // Left align
    commands.push(...Array.from('-'.repeat(lineWidth)).map(c => c.charCodeAt(0)));
    commands.push(0x0A);

    // User
    commands.push(...Array.from(`User          : ${data.user || 'N/A'}`).map(c => c.charCodeAt(0)));
    commands.push(0x0A);

    // PC
    commands.push(...Array.from(`PC            : ${data.pc || 'N/A'}`).map(c => c.charCodeAt(0)));
    commands.push(0x0A);

    // Separator
    commands.push(...Array.from('-'.repeat(lineWidth)).map(c => c.charCodeAt(0)));
    commands.push(0x0A);

    // Items
    if (items && Array.isArray(items)) {
        items.forEach(item => {
            commands.push(...Array.from(`  Description  : ${item.description || 'N/A'}`).map(c => c.charCodeAt(0)));
            commands.push(0x0A);
            const qty = item.qty || 0;
            const value = item.value || 0;
            commands.push(...Array.from(`  Quantity: ${qty}          Value: ${value}`).map(c => c.charCodeAt(0)));
            commands.push(0x0A);
        });
    }

    // Separator
    commands.push(...Array.from('-'.repeat(lineWidth)).map(c => c.charCodeAt(0)));
    commands.push(0x0A);

    // Reason
    commands.push(...Array.from(`Reason        : ${data.reason || 'N/A'}`).map(c => c.charCodeAt(0)));
    commands.push(0x0A);

    // Supervisor
    commands.push(...Array.from(`Supervisor    : ${data.supervisor || 'N/A'}`).map(c => c.charCodeAt(0)));
    commands.push(0x0A);

    // Separator
    commands.push(...Array.from('-'.repeat(lineWidth)).map(c => c.charCodeAt(0)));
    commands.push(0x0A);
    commands.push(0x0A); // Empty line

    // Footer
    commands.push(...Array.from('Software by www.chrilantech.com').map(c => c.charCodeAt(0)));
    commands.push(0x0A);
    commands.push(0x0A, 0x0A, 0x0A, 0x0A); // Feed lines
    commands.push(ESC, 0x64, 0x08); // Feed 8 lines
    commands.push(GS, 0x56, 0x00); // Full cut

    // Convert to base64
    const buffer = new Uint8Array(commands);
    return btoa(String.fromCharCode.apply(null, buffer));
}

export default {
    name: 'sales',
    state: {
        saleDiscount: {},
        currentSaleLines: [],
        latestInvoice: {},
        refundInv: null,
        discountSpecials: [],
        comboSpecials: [],
        multiSpecials: [],
        tip: 0,
        lpo: null,
        saleDetails: {},
        // Controls whether sale-level discount is redistributed across items
        distributeSaleLevelDiscount: false,
        isSaleProcessing: false,
        lastSaleChange: 0,
        lastSaleInvoice: null,
        showChangeNotification: false,
        openTableName: null,

    },
    getters: {
        getLatestInvoice: (state) => state.latestInvoice,
        getRefundInvoice: (state) => state.refundInv,
        getCurrentSale: (state) => state.currentSale,
        getDiscountSpecials: (state) => state.discountSpecials,
        getComboSpecials: (state) => state.comboSpecials,
        getMultiSpecials: (state) => state.multiSpecials,
        getSaleLines: (state) => state.currentSaleLines,
        getItemSpecialPrice: (state) => (itemPLU) => {
            // Pure function: compute potential special price for display only. No state mutations.
            let specialPrice = null;

            // Discount Special (flat special price)
            let disc = state.discountSpecials.find(sp => sp.Plu === itemPLU)
            if (disc) {
                specialPrice = Number(disc.SpePrice) || null;
            }

            // Combo Special: if item is part of combo and counterpart exists in current sale, show combo price
            if (!specialPrice) {
                const comboEntry = state.comboSpecials.find(sp => sp.plu === itemPLU);
                if (comboEntry) {
                    const specialID = comboEntry.special_ID;
                    const groupID = Number(comboEntry.group);
                    const otherGroup = groupID === 1 ? 2 : 1;
                    const otherItems = state.comboSpecials.filter(sp => sp.special_ID === specialID && Number(sp.group) === otherGroup);
                    const hasCounterpart = state.currentSaleLines.some(line => otherItems.find(oi => oi.plu === line.Plu));
                    if (hasCounterpart) {
                        try {
                            const counterpartPlu = state.currentSaleLines
                                .map(l => l.Plu)
                                .filter(plu => otherItems.some(oi => oi.plu === plu));
                            console.log('[ComboSpecial] Applying combo price', {
                                itemPLU,
                                specialID,
                                groupID,
                                price: comboEntry.Price,
                                counterpartPlu
                            });
                        } catch (e) {
                            console.warn('[ComboSpecial] Log failed', e);
                        }
                        specialPrice = Number(comboEntry.Price) || null;
                    }
                }
            }

            // Multi-Quantity Special: if any line for this PLU meets threshold, show special unit price
            if (!specialPrice) {
                const multi = state.multiSpecials.find(sp => sp.Plu === itemPLU);
                if (multi) {
                    const threshold = Number(multi.HowMany) || 0;
                    const unit = Number(multi.Price);
                    const hasEnough = state.currentSaleLines.some(line => line.Plu === itemPLU && Number(line.Qty) >= threshold);
                    if (hasEnough && threshold > 0) specialPrice = unit;
                }
            }

            return specialPrice;
        },
        getTipAmount: (state) => state.tip,
        getLPO: (state) => state.lpo,
        getSaleTotal: (state) => {
            let saleLines = state.currentSaleLines;
            if (!saleLines || saleLines.length === 0) return 0.00;

            return saleLines.map(line => {
                let total = (line.Qty * line.Unit) + (line.Qty * line.tourismLevy) - line.Discount;

                // Check for instructions for each line item
                if (line.instructions && line.instructions.length > 0) {
                    let instructionTotal = line.instructions.map(i => i.Price * i.count).reduce((acc, n) => Number(n) + Number(acc), 0);
                    total += Number(instructionTotal); // Multiply by Qty to account for multiple instances
                }

                return Number(total);
            }).reduce((sum, line) => Number(sum) + Number(line), 0);
        },
        getSaleDiscount: state => {
            if (!isEmpty(state.saleDiscount)) {
                return state.saleDiscount
            } else return {
                type: 'none', value: 0, total: 0
            }
        },
        getSaleUnits: state => state.currentSaleLines.length,
        getSaleVat: (state, getters) => {
            return (state.currentSaleLines || [])
                .map(l => Number(l.vat || 0))
                .reduce((sum, v) => Number(sum) + Number(v), 0);
        },
        getSubTotal: (state, getters) => {
            return getters.getSaleTotal - getters.getSaleVat
        },
        getSaleDetails: (state) => state.saleDetails,
        getLastSaleChange: (state) => state.lastSaleChange,
        getLastSaleInvoice: (state) => state.lastSaleInvoice,
        getShowChangeNotification: (state) => state.showChangeNotification,
        getSaleProcessingState: (state) => state.isSaleProcessing === true,
    },
    mutations: {
        updateSaleLinePrice: async (state, data) => {
            const storeDB = await getStoreDB();
            await Promise.all(state.currentSaleLines.map(async (line) => {
                let currentPrice = line.Unit;
                if (line.index === data.index) {
                    let stock1 = await Storage.getAllStock();
                    let item = stock1.find(stock => stock.PLU === data.Plu);
                    if (item) {
                        switch (data.priceType) {
                            case "Normal":
                                currentPrice = item.SellingIncl;
                                break;
                            case "At Cost":
                                currentPrice = item.InclCost;
                                break;
                            case "Price 1":
                                currentPrice = item.price1;
                                break;
                            case "Price 2":
                                currentPrice = item.price2;
                                break;
                            case "Price 3":
                                currentPrice = item.price3;
                                break;
                            case "Price 4":
                                currentPrice = item.price4;
                                break;
                            case "Price 5":
                                currentPrice = item.price5;
                                break;
                            default:
                                currentPrice = line.Unit;
                                break;
                        }

                    }
                }
                line.Unit = currentPrice;
                // Update base unit so specials recompute from the chosen price level
                line.baseUnit = Number(currentPrice);
                line.Line = (Number(currentPrice * Number(line.Qty)).toFixed(2)) - line.Discount;
                await updateLineVat(line, storeDB);
                return line;
            }));
            // After updating price level, re-evaluate specials
            store.commit('recalcSpecials');
            broadcastSaleUpdate(state);
            broadcastLedUpdate(state);
        },
        reduceQuantity: async (state, saleLine) => {
            const line = state.currentSaleLines.find(line => line.index === saleLine.index);
            if (line && line.Qty > 0) {
                const storeDB = await getStoreDB();
                line.Qty -= 1;
                line.Line = (Number(line.Unit) * Number(line.Qty)) - line.Discount;
                await updateLineVat(line, storeDB);
                if (line.Qty <= 0) {
                    state.currentSaleLines = state.currentSaleLines.filter(l => l.index !== line.index);
                    store.commit('reIndexSale');
                }
                // Re-evaluate specials after quantity reduction
                store.commit('recalcSpecials');
                broadcastSaleUpdate(state);
                broadcastLedUpdate(state);
            }
        },
        updateSaleLineQty: async (state, payload) => {
            const { saleLine, qty } = payload;
            const line = state.currentSaleLines.find(line => line.index === saleLine.index);
            if (line) {
                const storeDB = await getStoreDB();
                line.Qty = qty;
                line.Line = (Number(line.Unit) * Number(line.Qty)) - line.Discount;
                await updateLineVat(line, storeDB);
                // Re-evaluate specials after any explicit qty set
                store.commit('recalcSpecials');
                broadcastSaleUpdate(state);
                broadcastLedUpdate(state);
            }
        },
        updateSavedQty: (state) => {
            state.currentSaleLines.forEach(line => {
                line.savedQty = Number(line.Qty);
            });
        },
        updateSaleLineDiscount: async (state, payload) => {
            // Supports payloads:
            // - { saleLine, discount }  // backward compatibility: fixed amount
            // - { saleLine, value, mode } where mode in ['value','percent']
            const { saleLine } = payload;
            const line = state.currentSaleLines.find(l => l.index === saleLine.index);
            if (!line) return;

            // Compute base = (Unit + tourismLevy) * Qty + sum(instruction.Price * instruction.count)
            const qty = Number(line.Qty) || 0;
            const unit = Number(line.Unit) || 0;
            const levy = Number(line.tourismLevy || 0) || 0;
            let base = (unit + levy) * qty;
            if (line.instructions && line.instructions.length > 0) {
                const instructionTotal = line.instructions
                    .map(i => Number(i.Price) * Number(i.count || 1))
                    .reduce((acc, n) => Number(acc) + Number(n), 0);
                base += Number(instructionTotal);
            }
            base = Number(base) || 0;

            // Determine discount value
            let d = 0;
            if (payload.mode === 'percent' || payload.discountType === 'percent') {
                const pct = Math.max(0, Math.min(100, Number(payload.value ?? payload.discountAmount ?? 0)));
                d = Number(((pct / 100) * base).toFixed(2));
            } else if (payload.mode === 'value' || payload.discountType === 'value') {
                d = Number(payload.value ?? payload.discount ?? payload.discountAmount ?? 0);
            } else {
                // Backward compatibility: payload.discount as value
                d = Number(payload.discount ?? 0);
            }

            if (isNaN(d) || d < 0) d = 0;
            if (d > base) d = base;

            line.Discount = d;
            line.Line = Number((base - d).toFixed(2));
            const storeDB = await getStoreDB();
            await updateLineVat(line, storeDB);
            broadcastSaleUpdate(state);
            broadcastLedUpdate(state);
        },
        // Helper to broadcast sale state to customer display


        // ... existing code ...

        // Recalculate and apply specials based on current quantities and loaded specials
        recalcSpecials: async (state) => {
            // Helper: find discount special by PLU
            const findDiscount = (plu) => state.discountSpecials?.find(sp => sp.Plu === plu);
            const findMulti = (plu) => state.multiSpecials?.find(sp => sp.Plu === plu);

            state.currentSaleLines = state.currentSaleLines.map(line => {
                // ... (lines 253-294 logic intact) ...
                // Ensure baseUnit exists for reversion
                if (line.baseUnit == null) line.baseUnit = Number(line.Unit);

                // Reset to base
                line.Unit = Number(line.baseUnit);
                line.specialActive = false;
                line.specialType = null;
                line.specialNote = null;

                const qty = Number(line.Qty) || 0;

                // Apply Multi-Quantity Special first (min-set quantity logic)
                const multi = findMulti(line.Plu);
                if (multi) {
                    const threshold = Number(multi.HowMany) || 0;
                    const specialUnit = Number(multi.Price);
                    if (qty >= threshold && threshold > 0) {
                        line.Unit = specialUnit;
                        line.specialActive = true;
                        line.specialType = 'multi';
                        line.specialNote = `Special: ${threshold}+ @ ${specialUnit}`;
                    }
                }

                // If no multi applied, apply Discount Special (flat special price)
                if (!line.specialActive) {
                    const disc = findDiscount(line.Plu);
                    if (disc && disc.SpePrice != null) {
                        const specialUnit = Number(disc.SpePrice);
                        if (!isNaN(specialUnit) && specialUnit > 0) {
                            line.Unit = specialUnit;
                            line.specialActive = true;
                            line.specialType = 'discount';
                            line.specialNote = `Special Price @ ${specialUnit}`;
                        }
                    }
                }

                // If no other special applied, apply Combo Special
                if (!line.specialActive) {
                    const comboEntry = state.comboSpecials.find(sp => sp.plu === line.Plu);
                    if (comboEntry) {
                        const specialID = comboEntry.special_ID;
                        const groupID = Number(comboEntry.group);
                        const otherGroup = groupID === 1 ? 2 : 1;
                        const otherItems = state.comboSpecials.filter(sp =>
                            sp.special_ID === specialID && Number(sp.group) === otherGroup
                        );
                        const hasCounterpart = state.currentSaleLines.some(l =>
                            otherItems.find(oi => oi.plu === l.Plu)
                        );
                        if (hasCounterpart) {
                            const specialUnit = Number(comboEntry.Price);
                            if (!isNaN(specialUnit) && specialUnit > 0) {
                                line.Unit = specialUnit;
                                line.specialActive = true;
                                line.specialType = 'combo';
                                line.specialNote = `Combo Price @ ${specialUnit}`;
                            }
                        }
                    }
                }

                // Recompute line total (excluding instructions/levy to keep consistent with other mutations)
                line.Line = (Number(line.Unit) * qty) - Number(line.Discount || 0);
                return line;
            });

            const storeDB = await getStoreDB();
            await Promise.all(state.currentSaleLines.map(line => updateLineVat(line, storeDB)));

            // Broadcast update to customer display and LED
            broadcastSaleUpdate(state);
            broadcastLedUpdate(state);
        },

        // ...

        // applyDiscount: (state, payload) => {
        //     // ... existing applyDiscount logic ...
        //     // Normalize payload
        //     if (!payload) payload = { type: 'none', value: 0, total: 0 };
        //     state.saleDiscount = payload;
        //
        //     // If redistribution is disabled, do not touch line discounts
        //     if (!state.distributeSaleLevelDiscount) {
        //         broadcastSaleUpdate(state); // Ensure broadcast even if no redistribution
        //         broadcastLedUpdate(state);
        //         return;
        //     }
        //
        //     // Prepare helpers to compute pre-discount line totals
        //     const calcLineBase = (line) => {
        //         let total = (Number(line.Qty) * Number(line.Unit)) + (Number(line.Qty) * Number(line.tourismLevy || 0));
        //         if (line.instructions && line.instructions.length > 0) {
        //             const instructionTotal = line.instructions
        //                 .map(i => Number(i.Price) * Number(i.count))
        //                 .reduce((acc, n) => Number(acc) + Number(n), 0);
        //             total += Number(instructionTotal);
        //         }
        //         return Number(total);
        //     };
        //
        //     const lines = state.currentSaleLines || [];
        //     if (lines.length === 0) {
        //         broadcastSaleUpdate(state);
        //         broadcastLedUpdate(state);
        //         return;
        //     }
        //
        //     // Reset existing per-line discounts to avoid compounding
        //     let baseTotals = lines.map(line => calcLineBase({ ...line, Discount: 0 }));
        //     const baseSum = baseTotals.reduce((a, b) => Number(a) + Number(b), 0);
        //     if (baseSum <= 0) {
        //         // Nothing to discount
        //         lines.forEach((line) => {
        //             line.Discount = 0;
        //             line.Line = calcLineBase(line);
        //         });
        //         broadcastSaleUpdate(state);
        //         broadcastLedUpdate(state);
        //         return;
        //     }
        //
        //     let targetTotalDiscount = 0;
        //     let ratio = 0;
        //     if (payload.type === 'Percentage') {
        //         const pct = Math.max(0, Math.min(100, Number(payload.value || 0)));
        //         ratio = pct / 100;
        //         targetTotalDiscount = Number((baseSum * ratio).toFixed(2));
        //     } else if (payload.type === 'Value') {
        //         targetTotalDiscount = Math.max(0, Math.min(Number(payload.total || payload.value || 0), baseSum));
        //         ratio = targetTotalDiscount / baseSum;
        //     } else {
        //         // No discount
        //         lines.forEach((line) => {
        //             line.Discount = 0;
        //             line.Line = calcLineBase(line);
        //         });
        //         broadcastSaleUpdate(state);
        //         broadcastLedUpdate(state);
        //         return;
        //     }
        //
        //     // Distribute discount proportionally across lines; handle rounding residual on last line
        //     let accumulated = 0;
        //     const lastIndex = lines.length - 1;
        //     lines.forEach((line, i) => {
        //         const base = baseTotals[i];
        //         let lineDiscount = (i === lastIndex)
        //             ? Number((targetTotalDiscount - accumulated).toFixed(2))
        //             : Number((base * ratio).toFixed(2));
        //
        //         // Guard rails
        //         if (lineDiscount < 0) lineDiscount = 0;
        //         if (lineDiscount > base) lineDiscount = base;
        //
        //         accumulated = Number((accumulated + lineDiscount).toFixed(2));
        //
        //         line.Discount = lineDiscount;
        //         line.Line = Number((base - lineDiscount).toFixed(2));
        //     });
        //
        //     broadcastSaleUpdate(state);
        //     broadcastLedUpdate(state);
        // },

        /**
         * `clearSale` mutation — wipes ALL sale state in one go:
         *   - currentSaleLines  (cart items)
         *   - currentSale       (sale number)
         *   - saleDiscount      (sale-level discount)
         *   - tip               (tip amount)
         *   - openTableName     (open table reference)
         * It also persists an empty array to localStorage and clears
         * the customer-facing LED / screen displays.
         *
         * NOTE — investigation 2026-07-23 (Athena): This mutation is
         * NOT called by the Delivery click handler
         * (`processDelivery`). The Delivery handler only commits
         * `setTipAmount(0)`. `clearSale` is dispatched by the room
         * charge flow (PaymenScreen.vue#handleRoomSelected ~line 372)
         * and is functionally similar to the inline clearing inside
         * `processTransaction` after a successful payment
         * (~line 2101). If the Delivery button is perceived to clear
         * more than the tip, that side-effect is downstream of the
         * handler, not inside it.
         */
        clearSale: (state) => {
            state.currentSaleLines = [];
            state.currentSale = 0;
            state.saleDiscount = undefined;
            state.tip = 0;
            state.openTableName = null;
            persistSaleLinesToLocalStorage([], { immediate: true });
            const ledSettings = getLedSettings();
            const port = ledSettings.ledPort || 'COM2';
            const baudRate = ledSettings.ledBaudRate || 2400;
            if (window.screenAPI && window.screenAPI.clearDisplay) {
                window.screenAPI.clearDisplay().catch(e => console.error(e));
            }
            if (window.ledDisplay && window.ledDisplay.clear) {
                window.ledDisplay.clear(port, baudRate).catch(e => console.error('[LED] Clear failed:', e));
            }
        },

        // ...

        processInvoice: async (context, payload) => {
            // BUG NOTE (investigation 2026-07-11): This `processInvoice` action is the FIRST of
            // TWO definitions on the same module (the second one starts at line ~1849 in this
            // file). Vuex's `actions` object literal evaluates definitions in order, so the
            // second definition OVERWRITES this first one. The only dispatcher of processInvoice
            // is line 1795 inside `processTransaction` (`context.dispatch('processInvoice', invoice)`),
            // so it always lands on the second (reachable) implementation. THIS first
            // implementation is DEAD CODE -- it is never invoked at runtime, despite being fully
            // defined. It is retained here for historical context only.
            //
            // Note that even if it WERE reached, it would be a loss point: it defines
            // `recordPaidTable` but never CALLS it (no invocation anywhere in this function's
            // body). If you resurrect this code path, make sure to invoke `recordPaidTable`
            // after a successful payment response, otherwise the table will be marked paid in
            // localStorage but `removeTable` will never reach the table-server.
            let store = await Storage.getDefaultStore();
            let currency = await Storage.getBaseCurrency();
            let user = await Storage.getCurrentUser();
            // Capture table name early to prevent it from being cleared before we check it
            // Use robust validation like the second processInvoice - trim and check saleNumber > 0
            const openTableName = String(context.rootGetters['getOpenTableName'] || '').trim();
            const saleNumber = context.getters.getCurrentSale;

            const recordPaidTable = async ({ invoiceNo, offline }) => {
                // Use robust validation: trimmed table name and saleNumber must be > 0
                if (!openTableName || !saleNumber || Number(saleNumber) <= 0) return;
                try {
                    await paidTablesOutbox.addPaidTable({
                        saleNumber,
                        tableName: openTableName,
                        paymentType: payload?.paymentType || payload?.type || 'Unknown',
                        total: Number(payload?.total || 0),
                        invoiceNo: invoiceNo || null,
                        offline: !!offline
                    });
                } catch (e) {
                    console.warn('[Sales] Failed to record paid table', e);
                }
            };

            // Generate unique client sale ID for idempotency
            const clientSaleId = generateUUID();
            const enhancedPayload = {
                ...payload,
                clientSaleId,
                clientTimestamp: new Date().toISOString()
            };

            // For Electron devices, do a quick quality check before posting online.
            // If quality is poor, jump directly to offline queue flow.
            return Promise.resolve()
                .then(async () => {
                    if (!connectivityMonitor.assessConnectionQuality) return null;
                    const isElectronRuntime = typeof window !== 'undefined' &&
                        (!!window.electronBridge || !!window.hardwareIPC || !!window?.process?.versions?.electron);
                    if (!isElectronRuntime) return null;
                    const quality = await connectivityMonitor.assessConnectionQuality();
                    if (quality?.poor) {
                        console.log('[Sales][QualityGate][OfflineSwitch]', quality);
                        const err = new Error('Connection check failed. Switching to offline sale mode.');
                        err.code = 'ERR_NETWORK_QUALITY';
                        err.connectionQuality = quality;
                        throw err;
                    }
                    return null;
                })
                .then(() => postInvoiceThroughMainPc(store.StoreDB, enhancedPayload))
                .then(async ({ data }) => {
                    if (typeof data === 'string') {
                        let correctedData = data.replaceAll(/NaN/g, '0');
                        data = JSON.parse(correctedData);
                    }
                    if (data.status === 200) {
                        connectivityMonitor.setOnline(true);
                        try {
                            // Send Payment Success to Customer Display
                            if (window.screenAPI && window.screenAPI.sendPaymentData) {
                                // Get currency from store
                                const displayCurrency = store.getters.getBaseCurrency || { Symbol: 'K', Name: 'Kwacha' };

                                const paymentData = {
                                    grandTotal: enhancedPayload.total,
                                    change: enhancedPayload.change,
                                    method: enhancedPayload.paymentType || 'Cash',
                                    currency: displayCurrency
                                };

                                console.log('[CustomerDisplay] Sending payment summary:', paymentData);
                                window.screenAPI.sendPaymentData(paymentData).catch(e => console.error('[CustomerDisplay] Payment send failed:', e));
                            }

                            // Respect device settings for auto printing
                            const settings = await Storage.getDeviceSettings();
                            const shouldPrint = settings?.PrintReceipt !== false; // default true

                        } catch (e) {
                            console.error('Error processing invoice response:', e);
                        }
                    }
                    return data;
                });
        },

        generateSaleUUID: (state) => state.saleUUID = generateUUID(),
        setDiscountSpecials: async (state, payload) => {
            if (!payload) payload = await Storage.getDiscountSpecials()
            state.discountSpecials = payload;
            // Re-evaluate specials when list changes
            store.commit('recalcSpecials');
        },
        setSaleCurrency: async (state, payload) => {
            if (payload) state.saleCurrency = payload
            else state.saleCurrency = await Storage.getBaseCurrency();
        },

        setMultiSpecials: async (state, payload) => {
            if (!payload) payload = await Storage.getMultiSpecials()
            state.multiSpecials = payload;
            // Re-evaluate specials when list changes
            store.commit('recalcSpecials');
        },

        setComboSpecials: async (state, payload) => {
            if (!payload) payload = await Storage.getComboSpecials()
            state.comboSpecials = payload;
            // Re-evaluate specials when list changes
            store.commit('recalcSpecials');
        },

        setCurrentSale: (state, data) => {
            // Preserve exact sale number; truncation causes table ID mismatches
            // between create/open/update flows when offline.
            state.currentSale = data;
        },
        setOpenTableName: (state, payload) => state.openTableName = payload,
        setSaleDetails: (state, data) => state.saleDetails = data,
        setRefundInvoice: (state, data) => {
            state.refundInv = data;
            if (data.customer) {
                state.customer = data.customer;
            }
        },
        reIndexSale: (state) => {
            let lines = state.currentSaleLines;
            for (let i = 0; i < lines.length; i++) {
                lines[i].index = i;
            }
            state.currentSaleLines = lines;
        },
        setCurrentLines: async (state, data) => {
            const lines = Array.isArray(data) ? data : [];
            state.currentSaleLines = lines.map(l => ({
                ...l,
                baseUnit: l.baseUnit != null ? Number(l.baseUnit) : Number(l.Unit),
                specialActive: Boolean(l.specialActive) || false,
                specialType: l.specialType || null,
                specialNote: l.specialNote || null,
            }));
            const storeDB = await getStoreDB();
            await Promise.all(state.currentSaleLines.map(line => updateLineVat(line, storeDB)));
            // Ensure indices are consistent
            store.commit('reIndexSale');
            // Re-evaluate specials for restored lines
            store.commit('recalcSpecials');
        },
        addToCurrentSaleLines: (state, payload) => {
            let data = payload;
            // console.log('[addToCurrentSaleLines] Payload:', JSON.stringify(payload));

            // Ensure numeric values
            if (data.Unit) data.Unit = Number(data.Unit);
            if (data.Qty) data.Qty = Number(data.Qty);
            if (data.Price) data.Price = Number(data.Price); // sometimes Price is used instead of Unit?

            // Determine if this is the very first line being added to the current sale
            const wasEmpty = state.currentSaleLines.length === 0;

            // Determine base unit (normal price) for correct special reversion
            const stockItem = store.getters.getByPlu ? store.getters.getByPlu(data.Plu) : null;
            const inferredBaseUnit =
                data.baseUnit != null ? Number(data.baseUnit) :
                (stockItem && stockItem.SellingIncl != null) ? Number(stockItem.SellingIncl) :
                Number(data.Unit);

            // Merge key uses numeric Unit match (not Unit.toFixed(2) equality).
            // toFixed drift (3.76289 vs 3.76) used to spawn a duplicate Qty:1 line
            // instead of bumping the existing line (qty-reset-on-save bonus #4).
            let item = state.currentSaleLines.find(line => {
                return line.Plu === data.Plu &&
                    saleLineUnitsMatch(line.Unit, data.Unit) &&
                    line.Description === data.Description;
            });
            const settings = store.getters.getDeviceSettings || {};
            const separateLine = settings.SeperateLine === true || settings.SeparateLine === true;
            if (item && !separateLine && !item.voucher) {
                // preserve base unit if not set
                if (item.baseUnit == null) item.baseUnit = inferredBaseUnit;
                const addedQty = Number(payload.Qty || 0);
                item.Qty = Number(item.Qty) + (Number.isFinite(addedQty) ? addedQty : 0);
                item.Line = Number(((Number(item.Unit) * Number(item.Qty)) - Number(item.Discount || 0)).toFixed(2));
                const incomingInstructions = Array.isArray(payload.instructions) ? payload.instructions : [];
                const currentInstructions = Array.isArray(item.instructions) ? item.instructions : [];
                const mergedInstructions = [...incomingInstructions, ...currentInstructions];
                let edited = mergedInstructions.reduce((accumulator, msg) => {
                    if (accumulator.has(msg.CookingNum)) {
                        let m = accumulator.get(msg.CookingNum)
                        m.count += msg.count
                    } else {
                        accumulator.set(msg.CookingNum, msg);
                    }
                    return accumulator;
                }, new Map())
                item.instructions = [...edited.values()]
            } else {
                data.index = state.currentSaleLines.length + 1
                data.savedQty = 0;
                // seed baseUnit for future recalculations
                if (data.baseUnit == null) data.baseUnit = inferredBaseUnit;
                // initialize special flags
                data.specialActive = false;
                data.specialType = null;
                data.specialNote = null;
                data.Discount = Number(data.Discount || 0);
                if (!Number.isFinite(Number(data.Qty)) || Number(data.Qty) <= 0) data.Qty = 1;
                data.Qty = Number(data.Qty);
                data.Unit = Number(data.Unit || data.Price || 0);
                data.Line = Number(((Number(data.Unit) * Number(data.Qty)) - Number(data.Discount || 0)).toFixed(2));
                data.vat = Number(data.vat || 0);
                // Stable client line id so backend CurrentSale.SeperateLine uses
                // UPSERT-by-id (Path 1) instead of PLU-only fallback (Path 3).
                // Without this, same-PLU + different Unit collapses into one row.
                // docs: FRONTEND.md cart line identity; BE SeperateLine Path 1
                if (!data.id && !data.Id && !data.lineId && !data.currentSaleLineId) {
                    data.id = generateUUID();
                } else if (!data.id) {
                    data.id = data.Id || data.lineId || data.currentSaleLineId;
                }
                state.currentSaleLines.push(data);

                // If this was the first item added to an empty sale, clear any last sale change notification/state
                if (wasEmpty) {
                    store.commit('clearLastSaleChange');
                }
            }
            // Keep UI path fast: defer heavy VAT/special reprocessing
            scheduleSaleHeavyRecalc();
        },
        /**
         * Void one unit of a cart line (PIN-gated).
         *
         * Why call logItemVoid then gate local remove by salenum (#26827 + 22844):
         * - Always POST/queue the void first so BE gets an audit trail.
         * - salenum==0 (unsaved cart): local cart is source of truth — remove the
         *   unit even when BE returns businessReject (no saleline row yet; BE
         *   audit-only). Silent UX: no toast at all — the void already succeeded
         *   from the cashier's view (TicketApp #26827 Founder follow-up).
         * - salenum!=0 (saved sale): keep 22844 fail-closed — do not strip FE
         *   when BE fails to archive, or re-add+save pure-merge duplicates rows.
         *   Toast stays three-way (#26827): offline / businessReject / network.
         * docs: FRONTEND.md cart line identity; BE CartLineArchiveHelper pure-merge
         */
        voidItem: async (state, payload) => {
            // Idempotent: second void of the same index is a no-op once gone.
            let item = state.currentSaleLines.find(s => s.index === payload.index)
            if (!item) return;
            let validation = await validateSecurity(`Void`);
            if (!validation) return;

            let supervisor = await Storage.getSupervisor();
            const saleNum = Number(store.getters.getCurrentSale || state.currentSale || 0) || 0;
            // Unsaved cart lines have no DB saleline; local state owns removal.
            const isUnsavedCartLine = saleNum === 0;

            // B5b / ticket 22844: BE logItemVoid archives by currentsale.id (UUID).
            // salenum + saleline help legacy PLU fallback and partial-void qty paths.
            let voidObj = {
                VoidPlu: item.Plu,
                VoidDescritpiom: item.Description,
                VoidQty: 1,
                VoidValue: item.Unit,
                SuperVisor: supervisor,
                id: item.id || item.Id || item.lineId || item.currentSaleLineId || null,
                saleline: item.saleline != null ? item.saleline : (item.SaleLine != null ? item.SaleLine : null),
                salenum: saleNum,
                SaleNum: saleNum,
            }
            await Storage.setSupervisor('');

            // Always attempt audit API (or offline queue). Local remove rules below.
            // TicketApp #26827: never show generic "Check the connection" when
            // logItemVoid already reported offline queue or business reject.
            let voidResult = null;
            try {
                voidResult = await store.dispatch(`logItemVoid`, voidObj);
            } catch (err) {
                // Network/throw path — still remove unsaved lines; toast only for saved.
                console.error('[voidItem] logItemVoid threw', err);
                voidResult = { success: false, offline: false, businessReject: false };
            }

            if (!voidResult || !voidResult.success) {
                // salenum==0: silent success — line is removed locally; BE is best-effort.
                // Why: cashier already sees the item leave the cart; error toasts imply failure.
                if (isUnsavedCartLine) {
                    // fall through to local remove
                } else {
                    // Three-way toast contract for saved lines only (#26827):
                    // 1) offline — logItemVoid already toasted queue success/fail
                    // 2) businessReject — logItemVoid already toasted server message
                    // 3) network/unknown — show connection copy here only
                    if (!(voidResult && (voidResult.offline === true || voidResult.businessReject === true))) {
                        Toast.fire({
                            icon: 'error',
                            title: 'Void failed',
                            text: 'This item was not removed. Check the connection, then try again.',
                        });
                    }
                    // 22844: saved lines stay until BE accepts/queues the archive.
                    return;
                }
            }

            // Re-find by index after await — line may already be gone (double-click).
            item = state.currentSaleLines.find(s => s.index === payload.index)
            if (!item) return;

            item.Qty--;
            item.instructions = (item.instructions || []).map((s) => {
                let msg = (payload.instructions || []).find(m => m.CookingNum === s.CookingNum)
                if (msg) {
                    s.count -= msg.count
                }
                return s;
            });
            if (item.Qty <= 0) {
                let arr = state.currentSaleLines
                state.currentSaleLines = $utils.removeIf(arr, s => s.index !== payload.index)
            }
            await store.dispatch(`calculateTipAmount`)
            store.commit(`reIndexSale`)
            store.commit('recalcSpecials')
            broadcastSaleUpdate(state);
            broadcastLedUpdate(state);
        },

        /**
         * Void an entire cart line (PIN-gated).
         *
         * Same salenum-aware local-remove contract as voidItem (#26827 + 22844):
         * call logItemVoid for audit, always remove when salenum==0 (silent),
         * fail-closed when salenum!=0 with differentiated toasts.
         * docs: FRONTEND.md cart line identity; BE SupervisorOverrides.logItemVoid
         */
        voidLine: async (state, data) => {
            // Idempotent: already-removed index is a no-op.
            let item = state.currentSaleLines.find(s => s.index === data.index)
            if (!item) return;
            let validation = await validateSecurity(`Void`);
            if (!validation) return;

            let supervisor = await Storage.getSupervisor();
            const saleNum = Number(store.getters.getCurrentSale || state.currentSale || 0) || 0;
            const isUnsavedCartLine = saleNum === 0;

            // Same B5b line-id contract as voidItem — full-line void still
            // needs id so backend can archive the correct currentsale row.
            let voidObj = {
                VoidPlu: item.Plu,
                VoidDescritpiom: item.Description,
                VoidQty: item.Qty,
                VoidValue: item.Line,
                SuperVisor: supervisor,
                id: item.id || item.Id || item.lineId || item.currentSaleLineId || null,
                saleline: item.saleline != null ? item.saleline : (item.SaleLine != null ? item.SaleLine : null),
                salenum: saleNum,
                SaleNum: saleNum,
            }
            await Storage.setSupervisor('');

            // Same #26827 salenum-aware toast + remove as voidItem.
            let voidResult = null;
            try {
                voidResult = await store.dispatch(`logItemVoid`, voidObj);
            } catch (err) {
                console.error('[voidLine] logItemVoid threw', err);
                voidResult = { success: false, offline: false, businessReject: false };
            }

            if (!voidResult || !voidResult.success) {
                // salenum==0: silent local success; BE audit is best-effort (#26827).
                if (isUnsavedCartLine) {
                    // fall through to local remove
                } else {
                    if (!(voidResult && (voidResult.offline === true || voidResult.businessReject === true))) {
                        Toast.fire({
                            icon: 'error',
                            title: 'Void failed',
                            text: 'This item was not removed. Check the connection, then try again.',
                        });
                    }
                    return;
                }
            }

            // Second void of same index is a no-op once the first removed it.
            if (!state.currentSaleLines.find(s => s.index === data.index)) return;

            let arr = state.currentSaleLines
            state.currentSaleLines = $utils.removeIf(arr, s => {
                return s.index !== data.index;
            })
            await store.dispatch(`calculateTipAmount`)
            store.commit(`reIndexSale`)
            store.commit('recalcSpecials')
            broadcastSaleUpdate(state);
            broadcastLedUpdate(state);
        },
        addReceipt: (state, payload) => {
            state.latestInvoice = payload
            state.currentSaleLines = []
            state.saleDiscount = 0.00
            persistSaleLinesToLocalStorage([], { immediate: true });
        },
        setLPO: (state, payload) => {
            state.lpo = payload.lpoNumber;
            state.customer = payload.debtorID;
        },
        applyDiscount: (state, payload) => {
            // Normalize payload
            if (!payload) payload = { type: 'none', value: 0, total: 0 };
            state.saleDiscount = payload;

            // If redistribution is disabled, do not touch line discounts
            if (!state.distributeSaleLevelDiscount) {
                broadcastSaleUpdate(state);
                broadcastLedUpdate(state);
                return;
            }

            // Prepare helpers to compute pre-discount line totals
            const calcLineBase = (line) => {
                let total = (Number(line.Qty) * Number(line.Unit)) + (Number(line.Qty) * Number(line.tourismLevy || 0));
                if (line.instructions && line.instructions.length > 0) {
                    const instructionTotal = line.instructions
                        .map(i => Number(i.Price) * Number(i.count))
                        .reduce((acc, n) => Number(acc) + Number(n), 0);
                    total += Number(instructionTotal);
                }
                return Number(total);
            };

            const lines = state.currentSaleLines || [];
            if (lines.length === 0) {
                broadcastSaleUpdate(state);
                broadcastLedUpdate(state);
                return;
            }

            // Reset existing per-line discounts to avoid compounding
            let baseTotals = lines.map(line => calcLineBase({ ...line, Discount: 0 }));
            const baseSum = baseTotals.reduce((a, b) => Number(a) + Number(b), 0);
            if (baseSum <= 0) {
                // Nothing to discount
                lines.forEach((line) => {
                    line.Discount = 0;
                    line.Line = calcLineBase(line);
                });
                broadcastSaleUpdate(state);
                broadcastLedUpdate(state);
                return;
            }

            let targetTotalDiscount = 0;
            let ratio = 0;
            if (payload.type === 'Percentage') {
                const pct = Math.max(0, Math.min(100, Number(payload.value || 0)));
                ratio = pct / 100;
                targetTotalDiscount = Number((baseSum * ratio).toFixed(2));
            } else if (payload.type === 'Value') {
                targetTotalDiscount = Math.max(0, Math.min(Number(payload.total || payload.value || 0), baseSum));
                ratio = targetTotalDiscount / baseSum;
            } else {
                // No discount
                lines.forEach((line) => {
                    line.Discount = 0;
                    line.Line = calcLineBase(line);
                });
                broadcastSaleUpdate(state);
                broadcastLedUpdate(state);
                return;
            }

            // Distribute discount proportionally across lines; handle rounding residual on last line
            let accumulated = 0;
            const lastIndex = lines.length - 1;
            lines.forEach((line, i) => {
                const base = baseTotals[i];
                let lineDiscount = (i === lastIndex)
                    ? Number((targetTotalDiscount - accumulated).toFixed(2))
                    : Number((base * ratio).toFixed(2));

                // Guard rails
                if (lineDiscount < 0) lineDiscount = 0;
                if (lineDiscount > base) lineDiscount = base;

                accumulated = Number((accumulated + lineDiscount).toFixed(2));

                line.Discount = lineDiscount;
                line.Line = Number((base - lineDiscount).toFixed(2));
            });
            broadcastSaleUpdate(state);
            broadcastLedUpdate(state);
        },
        // clearSale: (state) => {
        //     state.currentSaleLines = [];
        //     state.currentSale = 0;
        //     state.saleDiscount = undefined;
        //     state.tip = 0;
        //     state.openTableName = null;
        //     const ledSettings = getLedSettings();
        //     const port = ledSettings.ledPort || 'COM2';
        //     const baudRate = ledSettings.ledBaudRate || 2400;
        //     if (window.screenAPI && window.screenAPI.clearDisplay) {
        //         window.screenAPI.clearDisplay().catch(e => console.error(e));
        //     }
        //     if (window.ledDisplay && window.ledDisplay.clear) {
        //         window.ledDisplay.clear(port, baudRate).catch(e => console.error('[LED] Clear failed:', e));
        //     }
        // },
        /**
         * `clearDiscount` mutation — only clears the sale-level discount.
         * Does NOT touch the tip, cart lines or sale number.
         */
        clearDiscount: (state) => state.saleDiscount = undefined,
        /**
         * `setTipAmount` mutation — sets the tip to `data`.
         * Does NOT touch cart lines, discount, sale number or table.
         *
         * NOTE — investigation 2026-07-23 (Athena): This mutation is
         * used by:
         *   - The Delivery click handler (`processDelivery`) to clear
         *     the tip when taking a delivery sale.
         *   - `calculateTipAmount` action (~line 1522) to apply the
         *     AutoTip% device setting to the sale total.
         *   - `processTransaction` action (~line 2101, ~line 2341,
         *     ~line 2407) to clear the tip after a successful payment.
         *   - The `clearSale` mutation (~line 1047) indirectly.
         * The Delivery click commits `setTipAmount(0)` only — no other
         * state is mutated by the Delivery handler itself.
         */
        setTipAmount: (state, data) => state.tip = data,
        setLastSaleChange: (state, payload) => {
            // Support both old format (just change amount) and new format (object with change and invoice)
            if (typeof payload === 'number') {
                state.lastSaleChange = payload;
                state.lastSaleInvoice = null;
            } else {
                state.lastSaleChange = payload.change || 0;
                state.lastSaleInvoice = payload.invoice || null;
            }
            state.showChangeNotification = true;
        },
        clearLastSaleChange: (state) => {
            state.lastSaleChange = 0;
            state.lastSaleInvoice = null;
            state.showChangeNotification = false;
        },
        setSaleProcessingState: (state, payload) => {
            state.isSaleProcessing = payload === true;
        },
    },
    actions: {
        loadFromLocalStorage: async (context) => {
            try {
                if (Array.isArray(context.state.currentSaleLines) && context.state.currentSaleLines.length > 0) {
                    return context.state.currentSaleLines;
                }
                const stored = localStorage.getItem('currentSaleLines');
                if (!stored) return [];
                const parsed = JSON.parse(stored);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    context.commit('setCurrentLines', parsed);
                    return parsed;
                }
            } catch (e) {
                console.warn('[sales] Failed to load sale lines from localStorage:', e);
            }
            return [];
        },
        clearLocalStorage: () => {
            try {
                localStorage.removeItem('currentSaleLines');
            } catch (e) {
                console.warn('[sales] Failed to clear localStorage sale lines:', e);
            }
        },
        calculateTipAmount: async (context) => {
            let settings = await Storage.getDeviceSettings();
            // Tip should be calculated from the current sale total (which already accounts for any line-level discounts)
            let saleTotal = context.getters.getSaleTotal;
            if (settings && settings.AutoTip > 0) {
                let tip = Number(settings.AutoTip);
                tip = (tip / 100) * saleTotal;
                context.commit(`setTipAmount`, tip)
            }
        },
        parseInvoice: async (context, payload) => {
            let user = await Storage.getCurrentUser();
            let settings = await Storage.getDeviceSettings();
            const pcName = await resolveHostname(settings);
            let tipAmount = context.getters.getTipAmount;
            let total = Number(payload.total) - tipAmount;
            let linesDiscount = 0;
            let items = context.state.currentSaleLines;
            let discountTotal = Number(context.getters.getSaleDiscount.total);
            for (let i = 0; i < items.length; i++) {
                let line = items[i];
                linesDiscount += line.Discount;
                items[i].saleLine = i + 1;
                items[i].saleline = i + 1;
                items[i].Line = ((Number(line.Qty) * Number(line.Unit)) + (Number(line.Qty) * Number(line.tourismLevy)) - Number(line.Discount)).toFixed(2);
                items[i].tourismLevy = (items[i].tourismLevy).toFixed(2) * Number(line.Qty);
            }
            if (context.state.lpo) {
                if (payload.paid === (total - discountTotal)) {
                    payload.paid = 0;
                }
                else if (payload.paid > (total - discountTotal)) {
                    payload.paid = payload.paid
                }
            }

            // Use the order number passed from processTransaction
            // This ensures the same order number is used for both the invoice and the printed slip
            const orderNumberCount = payload.orderNumberCount || null;

            const currentSaleNumber = Number(context.getters.getCurrentSale || context.state.currentSale || 0);
            const hasSaleLines = Array.isArray(context.state.currentSaleLines) && context.state.currentSaleLines.length > 0;
            const routeSaleNumber = Number(router?.currentRoute?.query?.saleNumber || 0);

            // FIX: When there's a route saleNumber (navigating from table/payment link), use it directly.
            // If we have a valid routeSaleNumber AND we have sale lines loaded, treat it as a table sale
            // to preserve the original table's saleNumber instead of creating a new one.
            const isTablePayment = (Number.isFinite(routeSaleNumber) && routeSaleNumber > 0) && hasSaleLines;

            const hasOpenTable = !!(
                context.state.openTableName ||
                context.rootGetters['getOpenTableName'] ||
                isTablePayment
            );
            const forcedSaleNumber = Number(payload?.forcedSaleNumber || 0);

            // Resolve saleNumber for the invoice payload.
            // hasOpenTable + currentSale=0 previously posted saleNumber:0 → backend 406
            // "no invoice lines specified" / invalid sale, leaving the cart unusable
            // (items stay, currentSale stays 0, Save-for-later then mints a new number).
            // docs: FRONTEND.md — invoice fail must preserve reserved saleNumber
            let saleNumber = hasOpenTable
                ? (Number.isFinite(currentSaleNumber) && currentSaleNumber > 0
                    ? currentSaleNumber
                    : (isTablePayment ? Math.trunc(routeSaleNumber) : 0))
                : (Number.isFinite(forcedSaleNumber) && forcedSaleNumber > 0 ? Math.trunc(forcedSaleNumber) : 0);

            // Prefer any already-known positive source before minting
            if (!(Number.isFinite(saleNumber) && saleNumber > 0)) {
                if (Number.isFinite(forcedSaleNumber) && forcedSaleNumber > 0) {
                    saleNumber = Math.trunc(forcedSaleNumber);
                } else if (Number.isFinite(currentSaleNumber) && currentSaleNumber > 0) {
                    saleNumber = Math.trunc(currentSaleNumber);
                } else if (isTablePayment && Number.isFinite(routeSaleNumber) && routeSaleNumber > 0) {
                    saleNumber = Math.trunc(routeSaleNumber);
                }
            }

            // Cart has lines but still no saleNumber — reserve one before post
            if (!(Number.isFinite(saleNumber) && saleNumber > 0) && hasSaleLines) {
                try {
                    const reserved = await context.dispatch('reserveNextSaleNumber');
                    const reservedNum = Math.trunc(Number(reserved) || 0);
                    if (reservedNum > 0) {
                        saleNumber = reservedNum;
                    }
                } catch (e) {
                    console.error('[parseInvoice] Failed to reserve saleNumber for cart with lines:', e);
                }
            }

            // Persist so a failed invoice leaves currentSale usable for Save-for-later
            if (Number.isFinite(saleNumber) && saleNumber > 0) {
                const bound = Math.trunc(saleNumber);
                if (Number(context.getters.getCurrentSale || context.state.currentSale || 0) !== bound) {
                    context.commit('setCurrentSale', bound);
                }
                saleNumber = bound;
            }

            let invoice = {
                PC: pcName,
                saleNumber: (Number.isFinite(saleNumber) && saleNumber > 0) ? saleNumber : 0,
                date: null,
                loyaltyBarcode: context.getters.getLoyaltyDetails.barcode,
                export: payload.type === `Export`,
                tot: context.getters.isTot,
                // Prefer Username; fail-fast — never "System" (#26328)
                user: requireSalesWriteUser(user, 'sales-module.parseInvoice'),
                lpo: (!payload.refund) ? context.state.lpo : payload.lpo,
                reference: payload.reference ? payload.reference : ``,
                customer: context.getters.getCustomer,
                paymentType: payload.type === `Export` ? `AccountSale` : payload.type,
                items: items,
                refundReason: payload.refundReason ? payload.refundReason : ``,
                refundReasonCode: payload.refundReasonCode ? payload.refundReasonCode : `06`,
                localId: context.state.saleUUID,
                invoice: (!payload.refund) ? 0 : payload.oldInv,
                discount: discountTotal,
                vat: (!payload.refund) ? parseFloat(context.getters.getSaleVat).toFixed(2) : 0,
                subTotal: (!payload.refund) ? parseFloat(context.getters.getSubTotal).toFixed(2) : parseFloat(total).toFixed(2),
                total: parseFloat(total),
                paidAmount: (!payload.refund) ? parseFloat(payload.paid).toFixed(2) : (parseFloat(total) + tipAmount).toFixed(2),
                change: (!payload.refund) ? parseFloat(payload.change).toFixed(2) : 0,
                units: (!payload.refund) ? context.getters.getSaleUnits : payload.items.length,
                refund: payload.refund,
                tip: tipAmount,
                orderNumber: orderNumberCount, // Include order number count in the invoice
                ...(payload.refund && { rate: payload.currencyRate }),
                bookingId: payload.bookingId,
                bookingOtpTransactionId: payload.bookingOtpTransactionId
            }

            // Attach customer details for offline receipts (Buyer Details should show even if store state is empty)
            try {
                const custId = invoice.customer;
                if (custId) {
                    let debtor = null;
                    const debtors = (store?.state?.accounts?.debtors) || [];
                    debtor = debtors.find(d => d.Id === custId) || null;

                    if (!debtor && typeof custId === 'string' && custId.startsWith('tmp_')) {
                        const offlineList = await Storage.getOfflineCustomers();
                        if (Array.isArray(offlineList)) {
                            debtor = offlineList.find(d => d.Id === custId) || null;
                        }
                    }

                    if (debtor) {
                        invoice.customerDetails = {
                            Name: debtor.Name || '',
                            Surname: debtor.Surname || '',
                            CellNumber: debtor.CellNumber || debtor.Phone || '',
                            vat: debtor.vat || debtor.TPIN || '',
                            Adress1: debtor.Adress1 || debtor.Address1 || '',
                            Area: debtor.Area || '',
                            Balance: typeof debtor.Balance === 'number' ? debtor.Balance : null
                        };
                    }
                }
            } catch (e) {
                // Non-blocking: if customer lookup fails, continue silently
            }


            if (payload.type === 'Split') {
                let splitPaidTotal = 0;
                splitPaidTotal += Number(payload.splitArray.cash || 0);
                splitPaidTotal += Number(payload.splitArray.card || 0);
                splitPaidTotal += Number(payload.splitArray.Kazang || 0);
                splitPaidTotal += Number(payload.splitArray.Spenn || 0);

                if (payload.splitArray.other) {
                    for (let i = 0; i < payload.splitArray.other.length; i++) {
                        splitPaidTotal += Number(payload.splitArray.other[i].amount || 0);
                    }
                }

                invoice.paidAmount = parseFloat(splitPaidTotal);

                // Recalculate change for split payment
                const totalToPay = invoice.total + invoice.tip;
                const change = splitPaidTotal - totalToPay;
                invoice.change = change > 0 ? parseFloat(change.toFixed(2)) : 0;

                invoice.split = [
                    {
                        method: 'cash',
                        amount: payload.splitArray.cash
                    }, {
                        method: 'card',
                        amount: payload.splitArray.card
                    },

                    {
                        method: 'Kazang',
                        amount: payload.splitArray.Kazang
                    },

                    {
                        method: 'Spenn',
                        amount: payload.splitArray.Spenn
                    },
                ];
                if (context.getters.getHasLoyalty) {
                    invoice.split.push({
                        method: 'Loyalty Program',
                        amount: payload.splitArray[`Loyalty Program`] || 0
                    })
                }
                for (let i = 0; i < payload.splitArray.other.length; i++) {
                    let method = payload.splitArray.other[i];
                    invoice.split.push({
                        method: method.name,
                        amount: method.amount
                    });
                }
            }

            if ((payload.type === 'Kazang' || (payload.type === 'Split' && payload.splitArray.Kazang > 0)) ||
                (payload.type === 'Spenn' || (payload.type === 'Split' && payload.splitArray.Spenn > 0))) {
                invoice.SecondRefrence = payload.SecondRefrence
            }

            if (payload.type === 'Kazang') {
                invoice.total += invoice.discount;
            }
            return invoice
        },
        processTransaction: async (context, payload) => {
            if (context.getters.getSaleProcessingState) {
                console.warn('[processTransaction] Ignored duplicate trigger while processing');
                return false;
            }
            context.commit('setSaleProcessingState', true);

            try {
                let isAllowed = await validateSecurity(`convertToInvoice`);
                if (!isAllowed) return false;

                if (payload.type === `AccountSale` || payload.type === `Export`) isAllowed = await validateSecurity(`AccountSale`);
                if (!isAllowed) return false;

                if (payload.type === `Cash`) isAllowed = await validateSecurity(`Cash`);
                if (!isAllowed) return false;

                if (payload.type === `Card`) isAllowed = await validateSecurity(`Card`);
                if (!isAllowed) return false;

                // Enforce Main-PC availability for all write operations (non-main devices).
                await ensureMainPcReachableForWrite();
                const isOnline = connectivityMonitor.getStatus();
                const currentSaleNumber = Number(context.getters.getCurrentSale || context.state.currentSale || 0);
                const hasSaleLines = Array.isArray(context.state.currentSaleLines) && context.state.currentSaleLines.length > 0;
                const routeSaleNumber = Number(router?.currentRoute?.query?.saleNumber || 0);

                // FIX: When there's a route saleNumber (navigating from table/payment link), use it directly.
                // The previous hasOpenTable logic required openTableName to be set, but when navigating from
                // TableDetails.pay() or using direct payment links, the table data is loaded and saleNumber is valid.
                // If we have a valid routeSaleNumber AND we have sale lines loaded, treat it as a table sale
                // to preserve the original table's saleNumber instead of creating a new one.
                const isTablePayment = (Number.isFinite(routeSaleNumber) && routeSaleNumber > 0) && hasSaleLines;

                const hasOpenTable = !!(
                    context.state.openTableName ||
                    context.rootGetters['getOpenTableName'] ||
                    isTablePayment
                );
                let reservedSaleNumber = 0;
                if (!hasOpenTable) {
                    try {
                        reservedSaleNumber = await context.dispatch('reserveNextSaleNumber', { strictOnline: isOnline });
                        if (!Number.isFinite(Number(reservedSaleNumber)) || Number(reservedSaleNumber) <= 0) {
                            throw new Error('Invalid sale number response');
                        }
                    } catch (e) {
                        await Swal.fire({
                            icon: 'error',
                            title: 'Cannot Process Sale',
                            text: `Failed to get next sale number from Main device: ${e?.message || e}`,
                            confirmButtonText: 'OK'
                        });
                        return false;
                    }
                }

                // Always generate a new UUID for each sale transaction
                context.commit('generateSaleUUID');

                // Generate order number once for this transaction
                // This will be used for both the invoice and the printed order slip
                // Convert to integer for consistency with backend
                let orderNumberCount = null;
                try {
                    const orderNumStr = await OrdersService.nextOrderNumber();
                    orderNumberCount = parseInt(orderNumStr, 10); // Convert "001" -> 1
                } catch (e) {
                    console.warn('[processTransaction] Failed to get order number:', e);
                }

                // Note: We don't check connectivity here because:
                // 1. The connectivity check may be unreliable (tests local favicon, not API server)
                // 2. We'll attempt the actual API call first and handle errors appropriately
                // 3. Offline mode will only be used if the API call fails with a network error
                $utils.showLoading();

                // Handle Room payments differently - send to sendToRoom endpoint
                // if (payload.type === 'Room' && payload.roomDetails) {
                //     return context.dispatch('sendToRoom', payload)
                //         .then(async res => {
                //             if (res) {
                //                 // Check hideNextSale setting
                //                 const settings = await Storage.getDeviceSettings();
                //
                //                 if (settings?.hideNextSale === true) {
                //                     // Route to POS screen
                //                     return router.push({ name: 'pos' });
                //                 } else {
                //                     // Default behavior: route to viewPayment
                //                     return router.push({ name: 'viewPayment' });
                //                 }
                //             }
                //         }).finally(() => $utils.hideLoading());
                // }

                // For all other payment types, create and process invoice
                // Pass the order number to parseInvoice
                let invoice = await context.dispatch('parseInvoice', { ...payload, orderNumberCount, forcedSaleNumber: reservedSaleNumber })
                return context.dispatch('processInvoice', invoice)
                    .then(async res => {
                        if (res) {
                            const settings = await Storage.getDeviceSettings();
                            const changeAmount = Number(invoice?.change) || 0;
                            const latestReceipt = context.getters.getLatestInvoice;
                            const invoiceNumber = latestReceipt?.invoice || latestReceipt?.saleId || latestReceipt?.id || null;
                            const stayOnPosAfterSale = settings?.hideNextSale === true || settings?.hideNextSale === 1;

                            if (window.ledDisplay && window.ledDisplay.showWithMode) {
                                const ledSettings = getLedSettings();
                                const port = ledSettings.ledPort || 'COM2';
                                const baudRate = ledSettings.ledBaudRate || 2400;
                                const displayValue = ledSettings.ledDisplayValue || 'SALE_TOTAL';
                                const paidAmount = Number(invoice?.paidAmount) || 0;
                                const saleTotal = Number(invoice?.total) || 0;

                                switch (displayValue) {
                                    case 'PAID_AMOUNT':
                                        if (paidAmount > 0) {
                                            window.ledDisplay.showWithMode('payment', paidAmount.toFixed(2), port, baudRate)
                                                .catch(e => console.error('[LED] Payment display failed:', e));
                                        }
                                        break;
                                    case 'CHANGE':
                                        if (changeAmount > 0) {
                                            window.ledDisplay.showWithMode('change', changeAmount.toFixed(2), port, baudRate)
                                                .catch(e => console.error('[LED] Change display failed:', e));
                                        }
                                        break;
                                    case 'SALE_TOTAL':
                                    default:
                                        window.ledDisplay.showWithMode('total', saleTotal.toFixed(2), port, baudRate)
                                            .catch(e => console.error('[LED] Total display failed:', e));
                                        break;
                                }
                            }

                            if (stayOnPosAfterSale) {
                                context.commit('setLastSaleChange', {
                                    change: changeAmount,
                                    invoice: invoiceNumber
                                });
                                return router.push({ name: 'pos' });
                            }

                            return router.push({ name: 'viewPayment' });
                        }
                    }).finally(() => $utils.hideLoading())
            } finally {
                context.commit('setSaleProcessingState', false);
            }
        },

        processInvoice: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            let currency = await Storage.getBaseCurrency();
            let user = await Storage.getCurrentUser();
            // Capture table name and sale number early to prevent them from being cleared before we check them
            const openTableName = String(
                context.getters.getOpenTableName ||
                context.rootGetters.getOpenTableName ||
                ''
            ).trim();
            // Capture sale number from multiple sources - prioritize route query (most reliable for table payments)
            // Route query sale number is set when navigating from table and doesn't change during the flow
            const routeSaleNumber = Number(router?.currentRoute?.query?.saleNumber || 0);
            const saleNumberCandidates = [
                Number(routeSaleNumber || 0),  // Prioritize route query first
                Number(payload?.saleNumber || 0),
                Number(payload?.SaleNum || 0),
                Number(context.getters.getCurrentSale || 0)
            ];
            const capturedSaleNumber = saleNumberCandidates.find(n => Number.isFinite(n) && n > 0) || 0;
            console.log('[processInvoice] Sale number candidates:', saleNumberCandidates, '-> captured:', capturedSaleNumber);
            console.log('[processInvoice] Route query saleNumber:', routeSaleNumber);
            let resolvedPaidTableCtx = null;
            const resolvePaidTableContext = async () => {
                if (resolvedPaidTableCtx) return resolvedPaidTableCtx;

                // Use captured sale number instead of reading from state (which may be cleared)
                const saleNumber = capturedSaleNumber;
                const normalizedSaleNumber = Math.trunc(Number(saleNumber) || 0);

                let localTable = null;
                if (normalizedSaleNumber > 0) {
                    try {
                        localTable = await tableServerClient.getTableBySaleNumber(normalizedSaleNumber, store.StoreDB);
                        console.log('[resolvePaidTableContext] getTableBySaleNumber result for', normalizedSaleNumber, ':', localTable);
                    } catch (e) {
                        console.log('[resolvePaidTableContext] getTableBySaleNumber error:', e);
                        localTable = null;
                    }
                } else {
                    console.log('[resolvePaidTableContext] normalizedSaleNumber is 0, skipping table lookup');
                }

                const tableName = String(
                    openTableName ||
                    localTable?.Name ||
                    localTable?.name ||
                    ''
                ).trim();
                const hasLocalTable = !!localTable;
                // Only consider it a table payment if there was actually an open table.
                // openTableName being non-empty indicates the user opened a table first.
                // If openTableName is empty, this is a direct sale - don''t treat returned
                // table data as a table that needs to be deleted.
                const isTablePayment = !!(openTableName && (tableName || hasLocalTable));
                console.log('[resolvePaidTableContext] openTableName:', openTableName, 'tableName:', tableName, 'hasLocalTable:', hasLocalTable, 'isTablePayment:', isTablePayment);

                resolvedPaidTableCtx = {
                    saleNumber: normalizedSaleNumber,
                    tableName,
                    openTableName,
                    hasLocalTable,
                    isTablePayment
                };
                return resolvedPaidTableCtx;
            };
            const recordPaidTable = async ({ invoiceNo, offline }) => {
                try {
                    const paidCtx = await resolvePaidTableContext();
                    if (!paidCtx.isTablePayment || paidCtx.saleNumber <= 0) return;
                    await paidTablesOutbox.addPaidTable({
                        saleNumber: paidCtx.saleNumber,
                        tableName: paidCtx.tableName || null,
                        paymentType: payload?.paymentType || payload?.type || 'Unknown',
                        total: Number(payload?.total || 0),
                        invoiceNo: invoiceNo || null,
                        offline: !!offline
                    });
                } catch (e) {
                    console.warn('[Sales] Failed to record paid table', e);
                }
            };
            const finalizePaidTable = async ({ invoiceNo, offline }) => {
                await recordPaidTable({ invoiceNo, offline });
                try {
                    const paidCtx = await resolvePaidTableContext();
                    console.log('[finalizePaidTable] paidCtx:', paidCtx);

                    // Use captured sale number - this was captured at the start of processInvoice
                    // before the sale state was cleared
                    const saleNumber = paidCtx.saleNumber;

                    // Only delete table if this was actually a table payment
                    // Check: isTablePayment OR hasLocalTable OR tableName is set
                    // BUG NOTE (investigation 2026-07-11): The `isTableSale` guard intentionally
                    // requires one of three signals -- an openTableName at processInvoice start,
                    // a hit in the local table-server cache, or a non-empty tableName from
                    // either source. If all three are false (e.g. the user paid a direct sale
                    // whose saleNumber happens to match a previously-vanished table's number,
                    // or if `openTableName` was cleared by a race before this function ran),
                    // the function SKIPS the `removeTable` call entirely. `recordPaidTable`
                    // above has already written to `paidTablesOutbox`, so the UI will filter
                    // the matching saleNumber out via AllTables.vue:472-477, but the backend
                    // `offline_tables` SQLite row stays. The next POST `/tables/create` for the
                    // same name hits the LIKE duplicate-name check in table-server.js:308-332
                    // and returns 409. This is the most likely loss point observed by users.
                    //
                    // FIX (post-2026-07-11 investigation): include the eagerly-captured
                    // `openTableName` (closure-scoped at processInvoice entry, line 2094-2098)
                    // as an authoritative fourth signal so a race where all three downstream
                    // signals are simultaneously false no longer masks a real table sale.
                    // Still requires a non-zero sale number + at least one positive signal, so
                    // a direct cash sale whose saleNumber happens to collide with a vanished
                    // table will not trigger a delete.
                    const hasAnyTableSignal = !!(
                        openTableName
                        || paidCtx.tableName
                        || paidCtx.hasLocalTable
                        || paidCtx.isTablePayment
                    );
                    const isTableSale = paidCtx.saleNumber > 0 && hasAnyTableSignal;
                    console.log('[finalizePaidTable] saleNumber:', saleNumber, 'isTableSale:', isTableSale);

                    if (saleNumber > 0 && isTableSale) {
                        console.log(`[finalizePaidTable] Deleting table for sale ${saleNumber}, isTablePayment: ${paidCtx.isTablePayment}, hasLocalTable: ${paidCtx.hasLocalTable}, tableName: ${paidCtx.tableName}`);
                        // Remove paid table from local table cache immediately.
                        // When offline, queue delete for later sync; when online, local-only remove.
                        await tableServerClient.removeTable(
                            saleNumber,
                            store.StoreDB,
                            !offline
                        );
                        console.log(`[finalizePaidTable] Successfully deleted table for sale ${saleNumber}`);

                        // Refresh the table list in Vuex state to reflect the deletion
                        try {
                            console.log('[finalizePaidTable] Refreshing table list after payment');
                            await context.dispatch('getAllTables');
                            console.log('[finalizePaidTable] Table list refreshed');
                        } catch (e) {
                            console.warn('[finalizePaidTable] Failed to refresh table list', e);
                        }
                    } else {
                        console.log(`[finalizePaidTable] Skipping - not a table sale. saleNumber: ${saleNumber}, isTableSale: ${isTableSale}`);
                    }
                } catch (e) {
                    console.warn('[Sales] Failed to remove paid table from local cache', e);
                }
            };

            // Generate unique client sale ID for idempotency
            const clientSaleId = generateUUID();
            const enhancedPayload = {
                ...payload,
                clientSaleId,
                clientTimestamp: new Date().toISOString()
            };
            // Refunds reverse a server-side invoice; queuing them offline would desync stock/credit notes.
            // Parity with Retail POS processInvoice / processOfflineInvoice refund guards.
            const isRefund = enhancedPayload.refund === true;
            const rejectRefundOffline = async (logReason) => {
                console.warn(`[Invoice] Refund blocked: ${logReason} (refunds cannot use Sale Vault)`);
                $utils.hideLoading();
                try {
                    await Swal.fire({
                        title: 'Internet required',
                        text: 'Refunds require an internet connection and cannot be processed offline. Please reconnect and try again.',
                        icon: 'warning',
                        confirmButtonText: 'OK'
                    });
                } catch (e) { /* ignore Swal dismiss */ }
                const refundErr = new Error('Refunds cannot be processed offline. Please reconnect and try again.');
                refundErr.code = 'REFUND_OFFLINE_BLOCKED';
                return Promise.reject(refundErr);
            };
            const applyLocalSaleStock = async ({ markDirty = false, reason = 'sale', rollbackSaleId = null } = {}) => {
                const saleItems = Array.isArray(enhancedPayload?.items) ? enhancedPayload.items : [];
                if (saleItems.length === 0) {
                    return { ok: true, updated: 0, dirty: 0, failed: 0, missing: 0 };
                }

                const stockResult = await Storage.applySaleStockDeductions(store.StoreDB, saleItems, {
                    markDirty,
                    reason
                });

                if (stockResult?.ok === false && rollbackSaleId) {
                    try {
                        await outbox.deleteOutboxItem(rollbackSaleId, store?.StoreDB);
                    } catch (rollbackErr) {
                        console.warn('[Sales] Failed to rollback outbox item after stock deduction failure', rollbackErr);
                    }
                }

                return stockResult;
            };
            const prepareOrderPrintLines = async (saleLines = []) => {
                const clonedLines = Array.isArray(saleLines)
                    ? saleLines.map(line => ({ ...line }))
                    : [];

                const unresolved = clonedLines.filter(line => !line.StockSetting && !line.stockSetting);
                await Promise.all(unresolved.map(async (line) => {
                    try {
                        const setting = await Storage.getStockSetting(line.Plu || line.plu);
                        if (setting) {
                            line.StockSetting = setting;
                            line.stockSetting = setting;
                        }
                    } catch (_) {
                    }
                }));

                return clonedLines;
            };
            const queueQuickSaleOrderSlip = ({ saleLines, orderNumber, saleNumber, mode }) => {
                Promise.resolve()
                    .then(async () => {
                        if (payload?.refund === true) return;
                        const paidCtx = await resolvePaidTableContext();
                        // Why: openTableName can be cleared by earlier commits (race per BUG NOTE
                        // lines 2183-2194), so check hasLocalTable / resolved tableName too.
                        // This matches the hardened pattern already used at line 2195 in this file.
                        if (paidCtx?.isTablePayment || paidCtx?.hasLocalTable ||
                            (paidCtx?.tableName && paidCtx.tableName.length > 0)) return;
                        const linesForPrint = await prepareOrderPrintLines(saleLines);
                        if (!Array.isArray(linesForPrint) || linesForPrint.length === 0) return;
                        // Why: real table name preferred over hardcoded 'Quick Sale' so the slip
                        // reflects the actual table when this code path is hit by a table-sale
                        // race that the strengthened guard above did not catch. Hardcoded label
                        // is only correct for true direct (non-table) sales.
                        const tableNameForSlip = (paidCtx?.tableName && paidCtx.tableName.length > 0)
                            ? paidCtx.tableName
                            : 'Quick Sale';
                        await OrdersService.printOrder(linesForPrint, {
                            tableName: tableNameForSlip,
                            orderNumber,
                            saleNumber
                        });
                    })
                    .catch((e) => {
                        console.warn(`[Sales] Local order print failed (${mode}):`, e);
                    });
            };

            // For Electron devices, do a quick quality check before posting online.
            // If quality is poor, jump directly to offline queue flow (sales only — not refunds).
            return Promise.resolve()
                .then(async () => {
                    if (isRefund && !connectivityMonitor.getStatus()) {
                        return rejectRefundOffline('device offline');
                    }
                    if (!connectivityMonitor.assessConnectionQuality) return null;
                    const isElectronRuntime = typeof window !== 'undefined' &&
                        (!!window.electronBridge || !!window.hardwareIPC || !!window?.process?.versions?.electron);
                    if (!isElectronRuntime) return null;
                    const quality = await connectivityMonitor.assessConnectionQuality();
                    if (quality?.poor) {
                        if (isRefund) {
                            return rejectRefundOffline('poor connection quality');
                        }
                        console.log('[Sales][QualityGate][OfflineSwitch]', quality);
                        const err = new Error('Connection check failed. Switching to offline sale mode.');
                        err.code = 'ERR_NETWORK_QUALITY';
                        err.connectionQuality = quality;
                        throw err;
                    }
                    return null;
                })
                .then(() => postInvoiceThroughMainPc(store.StoreDB, enhancedPayload))
                .then(async ({ data }) => {
                    if (typeof data === 'string') {
                        let correctedData = data.replaceAll(/NaN/g, '0');
                        data = JSON.parse(correctedData);
                    }
                    if (data.status === 200) {
                        connectivityMonitor.setOnline(true);
                        const responseData = data?.response || {};
                        const responseSaleNumber = data?.response?.saleNumber ?? data?.response?.SaleNum ?? data?.response?.saleNum;

                        queueQuickSaleOrderSlip({
                            saleLines: payload?.items,
                            orderNumber: payload.orderNumber,
                            saleNumber: responseSaleNumber ?? payload.saleNumber,
                            mode: 'online'
                        });

                        // Clear sale state as soon as server confirms success.
                        context.commit('setCurrentSale', 0);
                        context.commit('setTipAmount', 0);
                        context.commit('addReceipt', responseData);
                        context.commit('clearDiscount');
                        context.commit('setSaleDetails', {});
                        context.commit('setLoyaltyDetails', {});
                        context.commit('setOpenTableName', null);
                        // Also clear table-module openTableName to hide the "Open Table" banner
                        context.commit('setOpenTableName', null, { root: true });
                        context.commit('setCustomer', "");
                        context.commit('setCurrentLines', []);
                        context.commit('setLPO', {
                            debtorID: ``,
                            lpoNumber: undefined,
                        });
                        localStorage.removeItem('currentSaleLines');
                        Swal.close();

                        try {
                            // Respect device settings for auto printing
                            const settings = await Storage.getDeviceSettings();
                            const shouldPrint = settings?.PrintReceipt !== false; // default true
                            const shouldPrompt = settings?.PromptA4Print === true; // default false

                            if (shouldPrint) {
                                let doPrint = true;
                                if (shouldPrompt) {
                                    const res = await Swal.fire({
                                        title: 'Print receipt?',
                                        text: 'Would you like to print the receipt now?',
                                        icon: 'question',
                                        showCancelButton: true,
                                        confirmButtonText: 'Print',
                                        cancelButtonText: 'Skip',
                                        reverseButtons: true,
                                    });
                                    doPrint = res.isConfirmed === true;
                                }
                                if (doPrint) {
                                    await context.dispatch(`BtPrint`, { invoice: data.response.invoice, reprint: false });
                                }
                            }
                        } catch (e) {
                        }

                        // Run non-critical housekeeping in background so POS loader clears immediately
                        // after sale/print flow instead of waiting on local cache/table maintenance.
                        Promise.resolve()
                            .then(async () => {
                                await finalizePaidTable({ invoiceNo: data.response?.invoice, offline: false });
                                try {
                                    localStorage.removeItem(`tableReservedSaleNum_${store.StoreDB}`);
                                } catch (e) { }
                                bumpTableNextSaleNum(store.StoreDB, responseSaleNumber ?? payload.saleNumber ?? enhancedPayload.saleNumber);
                                try {
                                    const stockResult = await applyLocalSaleStock({
                                        markDirty: false,
                                        reason: 'sale_online'
                                    });
                                    if (stockResult?.ok === false) {
                                        console.warn('[Sales] Local stock deduction warning on online sale', stockResult);
                                    }
                                } catch (stockErr) {
                                    console.warn('[Sales] Failed to apply local stock deduction for online sale', stockErr);
                                }
                            })
                            .catch((maintenanceErr) => {
                                console.warn('[Sales] Post-sale maintenance failed', maintenanceErr);
                            });
                        return true;
                    }
                }).catch(async (err) => {
                    // Only fallback to offline mode for timeout or network errors
                    // For other errors (4xx, 5xx), propagate them normally
                    //
                    // FIX (invoice-fail cart): 4xx/406 MUST NOT mutate the cart.
                    // Do not call setCurrentSale(0), setCurrentLines([]), clearSale,
                    // or clear openTableName here — leave pre-attempt state intact
                    // so Save-for-later can reuse the reserved saleNumber.
                    // docs: FRONTEND.md — preserve cart on invoice business errors

                    const status = Number(err?.response?.status || 0);
                    const isTimeout = err.code === 'ECONNABORTED' ||
                        (err.message && err.message.toLowerCase().includes('timeout'));
                    const isNetworkError = err.code === 'ERR_NETWORK' ||
                        !err.response ||
                        err.message === 'Network Error';
                    const isUpstreamUnavailable = status === 502 || status === 503 || status === 504;

                    // If it's an HTTP error with a status code (not a network/timeout issue), don't fallback to offline
                    if (!isTimeout && !isNetworkError && !isUpstreamUnavailable && err.response) {
                        console.error('Server returned an error (not a connectivity issue):', err);
                        return errorHandler.tomcatError(err);
                    }

                    // Already rejected upstream (offline / quality gate) — do not show Swal twice.
                    if (err?.code === 'REFUND_OFFLINE_BLOCKED') {
                        return Promise.reject(err);
                    }
                    // Refunds must never enter the outbox — even if the POST dies mid-flight after going offline.
                    if (isRefund) {
                        return rejectRefundOffline('network/timeout (no offline fallback)');
                    }

                    console.warn('Invoice posting failed due to timeout/network/quality issue, falling back to offline mode:', err);

                    try {
                        connectivityMonitor.setOnline(false);
                        const settings = await Storage.getDeviceSettings();

                        const flag = settings.allowOfflineInvoicing;
                        if (!flag) {
                            try {
                                await Swal.fire({
                                    title: 'Sale Vault Disabled',
                                    text: 'This device is offline and Sale Vault is disabled. Please connect to the internet to process this sale.',
                                    icon: 'warning',
                                    confirmButtonText: 'OK'
                                });
                            } catch (e) {
                            }
                            $utils.hideLoading();
                            return Promise.reject(new Error('Offline invoicing is disabled. Sale cannot be processed while offline.'));
                        }

                        // Enforce offline time limit based on system settings
                        try {
                            const sys = (store && store.getters && store.getters.getSystemSettings) ? store.getters.getSystemSettings : await Storage.getSystemSettings();
                            const hours = (sys && (sys.maxOfflineHours || sys.MaxOfflineHours)) || 3;
                            const remainingMs = connectivityMonitor.getRemainingOfflineTime(Number(hours) * 60 * 60 * 1000);
                            if (remainingMs === 0) {
                                try {
                                    await Swal.fire({
                                        title: 'Offline Limit Reached',
                                        text: 'You can no longer continue with offline sales. Please connect to the internet to proceed.',
                                        icon: 'warning',
                                        confirmButtonText: 'OK'
                                    });
                                } catch (e) { }
                                $utils.hideLoading();
                                return Promise.reject(new Error('Offline limit reached. Please reconnect to continue.'));
                            }
                        } catch (e) {
                            // If any error occurs, proceed but log it
                            console.warn('Failed to evaluate offline time limit:', e);
                        }

                        // Offline fallback:
                        // If this is a table payment, keep using the table SaleNum.
                        const assignedFromMain = Number(
                            err?.response?.data?.assignedSaleNumber ??
                            err?.response?.data?.saleNumber ??
                            err?.response?.data?.SaleNum ??
                            0
                        );
                        const currentSaleNum = Number(context.getters.getCurrentSale || 0);
                        const hasOpenTable = !!openTableName;
                        const payloadSaleNum = Number(enhancedPayload?.saleNumber ?? enhancedPayload?.SaleNum ?? 0);
                        const shouldReuseTableSaleNum = hasOpenTable && Number.isFinite(currentSaleNum) && currentSaleNum > 0;
                        let offlineSaleNumber = currentSaleNum;
                        if (shouldReuseTableSaleNum) {
                            offlineSaleNumber = currentSaleNum;
                        } else if (Number.isFinite(payloadSaleNum) && payloadSaleNum > 0) {
                            offlineSaleNumber = Math.trunc(payloadSaleNum);
                        } else if (Number.isFinite(assignedFromMain) && assignedFromMain > 0) {
                            offlineSaleNumber = Math.trunc(assignedFromMain);
                        } else {
                            throw new Error('Main device did not allocate a sale number. Cannot queue offline sale.');
                        }
                        enhancedPayload.saleNumber = offlineSaleNumber;
                        enhancedPayload.SaleNum = offlineSaleNumber;
                        enhancedPayload.orderNumber = offlineSaleNumber;
                        bumpTableNextSaleNum(store.StoreDB, offlineSaleNumber);

                        const saleId = await outbox.enqueueSale(
                            enhancedPayload,
                            store.StoreDB,
                            {
                                cashierName: user?.Username || 'Unknown',
                                cashierId: user?.UserId || null,
                                appVersion: '1.0.0',
                                failedOnlineAttempt: true,
                                error: 'Connection issue'
                            }
                        );

                        try {
                            localStorage.removeItem(`tableReservedSaleNum_${store.StoreDB}`);
                        } catch (e) { }

                        if (saleId === null || saleId === undefined) {
                            console.warn('Failed to enqueue offline sale. Sale cannot be queued for sync.');
                            Toast.fire({
                                icon: 'error',
                                title: 'Offline Save Failed',
                                text: 'Unable to queue this sale for sync. Please try again.'
                            });
                            return errorHandler.tomcatError({ message: 'Unable to queue offline sale for sync' });
                        }

                        const offlineStockResult = await applyLocalSaleStock({
                            markDirty: true,
                            reason: 'sale_offline',
                            rollbackSaleId: saleId
                        });
                        if (offlineStockResult?.ok === false) {
                            throw new Error('Unable to update local stock for offline sale');
                        }

                        // Create provisional receipt data using the generated saleId
                        const provisionalReceipt = {
                            invoice: saleId,
                            // Offline sale number should match the generated 10-digit number
                            saleNumber: enhancedPayload?.saleNumber || offlineSaleNumber || saleId,
                            clientSaleId,
                            saleId,
                            storeDB: store?.StoreDB || '',
                            provisional: true,
                            offline: true,
                            total: payload.total || 0,
                            paidAmount: payload.paidAmount || 0,
                            change: payload.change || 0,
                            timestamp: new Date().toISOString()
                        };

                        queueQuickSaleOrderSlip({
                            saleLines: enhancedPayload?.items,
                            orderNumber: enhancedPayload.orderNumber,
                            saleNumber: enhancedPayload?.saleNumber || saleId,
                            mode: 'offline'
                        });

                        await finalizePaidTable({ invoiceNo: provisionalReceipt.invoice, offline: true });

                        // Print provisional receipt if possible
                        try {
                            await context.dispatch('printProvisionalReceipt', {
                                receipt: provisionalReceipt,
                                payload: enhancedPayload
                            });
                        } catch (printErr) {
                            console.error('Failed to print provisional receipt:', printErr);
                        }

                        // Show notification
                        Toast.fire({
                            icon: 'warning',
                            title: 'Connection Issue',
                            text: 'Sale queued and will sync automatically'
                        });

                        // Clear sale and proceed
                        return Promise.all([
                            context.commit('setCurrentSale', 0),
                            context.commit('setTipAmount', 0),
                            context.commit('addReceipt', provisionalReceipt),
                            context.commit('clearDiscount'),
                            context.commit('setSaleDetails', {}),
                            context.commit('setLoyaltyDetails', {}),
                            context.commit('setCurrentSale', 0),
                            context.commit('setOpenTableName', null),
                            context.commit('setOpenTableName', null, { root: true }),
                            context.commit('setCustomer', ""),
                            context.commit('setCurrentLines', []),
                            context.commit('setLPO', {
                                debtorID: ``,
                                lpoNumber: undefined,
                            }),
                        ]).then(() => {
                            Swal.close();
                            return true;
                        });
                    } catch (outboxErr) {
                        console.error('Failed to enqueue sale after network error:', outboxErr);
                        return errorHandler.tomcatError(
                            new Error('Unable to save sale offline. Please retry the sale.'),
                            false,
                            'Offline Save Failed'
                        );
                    }
                })
        },

        /**
         * Send sale to room - uses exact same payload as saving a table
         * Just add the room number to the table payload structure
         */
        sendToRoom: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            let user = await Storage.getCurrentUser();
            let saleLines = context.getters.getSaleLines;
            const pcName = await resolveHostname();

            // Calculate line totals - same as saveNewTable
            for (let i = 0; i < saleLines.length; i++) {
                let line = saleLines[i];
                saleLines[i].Line = (Number(line.Qty) * Number(line.Unit)) - Number(line.Discount);
            }

            // Use exact same payload structure as saveNewTable (from table-module.js)
            // Just add room number field
            const roomPayload = {
                SaleNum: context.getters.getCurrentSale,
                Name: context.rootGetters['getOpenTableName'],
                RoomNumber: payload.roomDetails.roomNumber,
                User: user.Username,
                PC: pcName,
                items: saleLines,
                Customer: payload.Customer || null,
                discount: parseFloat(context.getters.getSaleDiscount.total || 0),
            };

            try {
                // Send to sendToRoom endpoint
                const response = await sendToRoomThroughMainPc(store.StoreDB, roomPayload);

                if (response.data && response.data.status === 200) {
                    // Clear sale data after successful send
                    await Promise.all([
                        context.commit('setCurrentSale', 0),
                        context.commit('setTipAmount', 0),
                        context.commit('setSaleDetails', {}),
                        context.commit('setLoyaltyDetails', {}),
                        context.commit('setCurrentSale', 0),
                        context.commit('setOpenTableName', null),
                        context.commit('setOpenTableName', null, { root: true }),
                        context.commit('setCustomer', ""),
                        context.commit('setCurrentLines', []),
                        context.commit('setLPO', {
                            debtorID: ``,
                            lpoNumber: undefined,
                        }),
                        context.commit('clearDiscount'),
                    ]);

                    localStorage.removeItem('currentSaleLines');

                    // Optionally print receipt
                    try {
                        const settings = await Storage.getDeviceSettings();
                        const shouldPrint = settings?.PrintReceipt !== false;
                        const shouldPrompt = settings?.PromptA4Print === true;

                        if (shouldPrint) {
                            let doPrint = true;
                            if (shouldPrompt) {
                                const res = await Swal.fire({
                                    title: 'Print receipt?',
                                    text: 'Would you like to print the receipt now?',
                                    icon: 'question',
                                    showCancelButton: true,
                                    confirmButtonText: 'Print',
                                    cancelButtonText: 'Skip',
                                    reverseButtons: true,
                                });
                                doPrint = res.isConfirmed === true;
                            }
                            if (doPrint) {
                                await context.dispatch(`BtPrint`, { invoice: response.data.response, reprint: false });
                            }
                        }
                    } catch (printError) {
                        console.warn('Failed to print receipt:', printError);
                    }

                    Swal.close();
                    return true;
                } else {
                    throw new Error('Failed to send to room');
                }
            } catch (error) {
                console.error('sendToRoom failed:', error);
                throw error;
            }
        },

        /**
         * Renders ESC/POS commands for offline provisional receipts (initial offline
         * sale and Offline Invoices reprint). Reads VAT values from the payload but
         * may derive print-only primitives (printVat, printExclTotal, lineVat) when
         * the selected store's onVat flag is false. MUST NOT mutate the payload or
         * its items — the same payload is enqueued to the outbox and synced to the
         * server with full VAT intact.
         * @param {Object} context Vuex action context (uses rootGetters)
         * @param {Object} args { receipt, payload }
         */
        printProvisionalReceipt: async (context, { receipt, payload }) => {
            // Print provisional receipt for offline sales
            // This creates a proper ESC/POS formatted receipt with "OFFLINE" branding
            try {
                let store = await Storage.getRegistrationDetails();
                let settings = await Storage.getDeviceSettings();
                let user = await Storage.getCurrentUser();
                console.log("settings ", store);

                console.log("payload", settings);

                console.log("reciep", receipt);

                // Respect receipt print settings
                const shouldPrint = settings?.PrintReceipt !== false; // default true
                const shouldPrompt = settings?.PromptA4Print === true; // default false
                if (!shouldPrint) {
                    return true; // Skip printing silently
                }
                if (shouldPrompt) {
                    const res = await Swal.fire({
                        title: 'Print receipt?',
                        text: 'Would you like to print the receipt now?',
                        icon: 'question',
                        showCancelButton: true,
                        confirmButtonText: 'Print',
                        cancelButtonText: 'Skip',
                        reverseButtons: true,
                    });
                    if (!res.isConfirmed) return true;
                }

                // Map invoice structure to receipt structure
                // payload has: items (array), vat, subTotal, total, paidAmount, change, units, paymentType
                const invoiceLines = payload.items || [];
                const exclTotal = Number(payload.subTotal || 0);
                const taxTotal = Number(payload.vat || 0);
                const total = Number(payload.total || receipt.total || 0);
                // Print-side gate: derive VAT-print values from store.onVat.
                // These are PRINT-SIDE ONLY — the payload/outbox keeps full VAT for sync.
                const onVat = context.rootGetters.getOnVatStatus === true;
                const printVat = onVat ? Number(payload.vat || 0) : 0;
                const printExclTotal = onVat ? Number(payload.subTotal || 0) : total;
                const paidAmount = Number(payload.paidAmount || receipt.paidAmount || 0);
                const change = Number(paidAmount - total);
                const paymentType = payload.paymentType || 'Cash';
                const totalUnits = Number(payload.units || 0);

                // Helper to generate ESC/POS commands
                const ESC = 0x1B;
                const GS = 0x1D;

                // Determine line width based on settings
                const receiptType = settings.ReceiptType || 'Normal';
                const is55mm = receiptType === '55ml';
                const lineWidth = is55mm ? 32 : 48;

                // Build ESC/POS command buffer
                const commands = [];

                // Initialize printer
                commands.push(ESC, 0x40);

                // Store name - Bold, Center, Large
                commands.push(ESC, 0x61, 0x01); // Center align
                commands.push(ESC, 0x45, 0x01); // Bold on
                commands.push(GS, 0x21, 0x11); // Double width & height
                console.log('[Offline Print] store:', store);
                console.log('[Offline Print] settings:', settings);
                const storeName =
                    settings?.ShopName ||
                    store?.StoreName ||
                    store?.Name ||
                    'Store';
                commands.push(...Array.from(storeName).map(c => c.charCodeAt(0)));
                commands.push(0x0A); // Line feed
                commands.push(GS, 0x21, 0x00); // Reset size
                commands.push(ESC, 0x45, 0x00); // Bold off

                // Location
                const location =
                    settings?.addr ||
                    store?.Addr1 ||
                    store?.Address ||
                    store?.Location ||
                    '';
                if (location) {
                    commands.push(...Array.from(location).map(c => c.charCodeAt(0)));
                    commands.push(0x0A);
                }
                const location2 =
                    settings?.addr2 ||
                    store?.Addr2 ||
                    '';
                if (location2) {
                    commands.push(...Array.from(location2).map(c => c.charCodeAt(0)));
                    commands.push(0x0A);
                }


                let tpin =
                    settings?.TPIN ||
                    settings?.Tpin ||
                    settings?.tpin ||
                    store?.TPIN ||
                    store?.Tpin ||
                    store?.tpin ||
                    store?.VatNo ||
                    '';
                if (!tpin) {
                    const addr3 = settings?.Addr3 || store?.Addr3 || settings?.addr3 || store?.addr3 || '';
                    const match = String(addr3).match(/\b\d{6,}\b/);
                    if (match) tpin = match[0];
                }
                if (tpin) {
                    commands.push(...Array.from(`TPIN : ${tpin}`).map(c => c.charCodeAt(0)));
                    commands.push(0x0A);
                }

                // Phone
                const phone =
                    settings?.Cell ||
                    settings?.cell ||
                    settings?.Phone ||
                    settings?.phone ||
                    store?.Cell ||
                    store?.CellNumber ||
                    store?.Phone ||
                    '';
                if (phone) {
                    commands.push(...Array.from(`Cell : ${phone}`).map(c => c.charCodeAt(0)));
                    commands.push(0x0A);
                }


                // OFFLINE RECEIPT indicator - Large, Bold, Center
                commands.push(ESC, 0x64, 0x01); // EMPTY LINE
                commands.push(ESC, 0x61, 0x01); // Center
                commands.push(ESC, 0x45, 0x01); // Bold on
                commands.push(...Array.from('Tax Invoice').map(c => c.charCodeAt(0)));
                commands.push(0x0A);
                commands.push(GS, 0x21, 0x00); // Reset size
                commands.push(ESC, 0x45, 0x00); // Bold off
                commands.push(ESC, 0x64, 0x01); // EMPTY LINE

                // Left align for rest of receipt
                commands.push(ESC, 0x61, 0x00);

                // Date and time
                const now = new Date(receipt.timestamp);
                const dateStr = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                const timeStr = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                commands.push(...Array.from(`Date ${dateStr}`).map(c => c.charCodeAt(0)));
                commands.push(0x0A);
                commands.push(...Array.from(`Time ${timeStr}`).map(c => c.charCodeAt(0)));
                commands.push(0x0A);

                // Invoice number
                commands.push(...Array.from(`Invoice No.: ${receipt.invoice}`).map(c => c.charCodeAt(0)));
                commands.push(0x0A);
                commands.push(ESC, 0x64, 0x01);

                // Buyer Details (if any)
                try {
                    const custId = payload?.customer || context.getters.getCustomer;
                    if (custId) {
                        let debtor = payload?.customerDetails || null;
                        if (!debtor) {
                            // Attempt to find debtor details from store
                            const debtors = (store?.state?.accounts?.debtors) || [];
                            debtor = debtors.find(d => d.Id === custId) || null;
                        }
                        const buyerNameRaw = debtor?.Name || 'Customer';
                        const buyerSurname = debtor?.Surname || '';
                        const buyerName = `${buyerNameRaw} ${buyerSurname}`.trim() || 'Customer';
                        const buyerTPIN = debtor?.vat || debtor?.TPIN || '';
                        const buyerAddr = debtor?.Adress1 || debtor?.Address1 || debtor?.Area || '';
                        const buyerPhone = debtor?.CellNumber || debtor?.Phone || '';
                        const buyerBalance = typeof debtor?.Balance === 'number' ? debtor.Balance : null;

                        // Section header
                        commands.push(ESC, 0x61, 0x01); // Center
                        commands.push(ESC, 0x45, 0x01); // Bold
                        commands.push(...Array.from('Buyer Details').map(c => c.charCodeAt(0)));
                        commands.push(0x0A);
                        commands.push(ESC, 0x45, 0x00); // Bold off
                        commands.push(ESC, 0x61, 0x00); // Left

                        // Name
                        commands.push(...Array.from(`Name: ${buyerName}`).map(c => c.charCodeAt(0)));
                        commands.push(0x0A);

                        // TPIN if present
                        if (buyerTPIN) {
                            commands.push(...Array.from(`TPIN: ${buyerTPIN}`).map(c => c.charCodeAt(0)));
                            commands.push(0x0A);
                        }

                        // Address if present
                        if (buyerAddr) {
                            commands.push(...Array.from(`Address: ${buyerAddr}`).map(c => c.charCodeAt(0)));
                            commands.push(0x0A);
                        }

                        // Contact if present
                        if (buyerPhone) {
                            commands.push(...Array.from(`Contact: ${buyerPhone}`).map(c => c.charCodeAt(0)));
                            commands.push(0x0A);
                        }

                        // Balance if numeric
                        if (buyerBalance !== null) {
                            commands.push(...Array.from(`Balance: K${Number(buyerBalance).toFixed(2)}`).map(c => c.charCodeAt(0)));
                            commands.push(0x0A);
                        }

                        // Spacer
                        commands.push(ESC, 0x64, 0x01);
                    }
                } catch (e) {
                    // Non-blocking: if buyer details fail, continue silently
                }

                // Items header
                commands.push(ESC, 0x61, 0x01);
                commands.push(ESC, 0x45, 0x01); // Bold
                commands.push(...Array.from('Items').map(c => c.charCodeAt(0)));
                commands.push(0x0A);
                commands.push(ESC, 0x45, 0x00);


                // Separator line
                commands.push(...Array.from('-'.repeat(lineWidth)).map(c => c.charCodeAt(0)));
                commands.push(0x0A);

                // Sale items - use items array from payload
                if (invoiceLines && Array.isArray(invoiceLines) && invoiceLines.length > 0) {
                    console.log('[Offline Print] item VATs:', invoiceLines.map(l => ({
                        Plu: l.Plu,
                        vat: l.vat,
                        Unit: l.Unit,
                        Qty: l.Qty
                    })));
                    invoiceLines.forEach(line => {
                        const plu = (line.Plu || '').substring(0, 6);
                        const desc = line.Description || 'Item';
                        const price = Number(line.Line || 0).toFixed(2);
                        const qty = line.Qty || 1;
                        const unitNum = Number(line.Unit || 0);
                        const unit = unitNum.toFixed(2);
                        const originalPrice = Number(
                            line.originalPrice ??
                            line.baseUnit ??
                            line.NormalPrice ??
                            line.RetailPrice ??
                            line.Unit ??
                            0
                        );
                        const hasPriceDrop = originalPrice > 0 && originalPrice > (unitNum + 0.0001);
                        const isSpecial = line.specialActive === true || !!line.specialType || hasPriceDrop;

                        // Build item description - add SPECIAL indicator if active
                        let itemDesc = desc;
                        if (isSpecial) {
                            itemDesc = `${desc} [SPECIAL]`;
                        }

                        // PLU and description with price right-aligned
                        const itemLine = `${plu}   ${itemDesc}`;
                        const spaces = Math.max(1, lineWidth - itemLine.length - price.length);
                        commands.push(...Array.from(itemLine).map(c => c.charCodeAt(0)));
                        commands.push(...Array.from(' '.repeat(spaces)).map(c => c.charCodeAt(0)));
                        commands.push(...Array.from(`K${price}`).map(c => c.charCodeAt(0)));

                        // Qty and unit price with VAT
                        commands.push(ESC, 0x61, 0x00);
                        const vat = (onVat ? Number(line.vat || 0) : 0).toFixed(3);
                        const qtyLine = `${qty} Qty ${unit} Each Vat ${vat}`;
                        commands.push(...Array.from(qtyLine).map(c => c.charCodeAt(0)));
                        commands.push(0x0A);

                        // If special, show the original price and savings
                        if (isSpecial && hasPriceDrop) {
                            const savings = (originalPrice - unitNum) * qty;
                            const wasPriceLine = `ON SPECIAL WAS: K${originalPrice.toFixed(2)}  SAVED: K${savings.toFixed(2)}`;
                            commands.push(ESC, 0x45, 0x01); // Bold
                            commands.push(...Array.from(wasPriceLine).map(c => c.charCodeAt(0)));
                            commands.push(ESC, 0x45, 0x00); // Bold off
                            commands.push(0x0A);
                        } else if (isSpecial && originalPrice > 0) {
                            const wasPriceLine = `ON SPECIAL WAS: K${originalPrice.toFixed(2)}`;
                            commands.push(...Array.from(wasPriceLine).map(c => c.charCodeAt(0)));
                            commands.push(0x0A);
                        }
                        if (isSpecial && line.specialNote) {
                            commands.push(...Array.from(String(line.specialNote)).map(c => c.charCodeAt(0)));
                            commands.push(0x0A);
                        }

                        // Separator line after each item
                        commands.push(...Array.from('-'.repeat(lineWidth)).map(c => c.charCodeAt(0)));
                        commands.push(0x0A);
                    });
                }

                // Totals
                commands.push(ESC, 0x61, 0x02);

                commands.push(ESC, 0x45, 0x01); // Bold
                commands.push(...Array.from(`Excl Total K${printExclTotal.toFixed(3)}`).map(c => c.charCodeAt(0)));
                commands.push(0x0A);
                commands.push(...Array.from(`Tax Total K${printVat.toFixed(3)}`).map(c => c.charCodeAt(0)));
                commands.push(0x0A);
                commands.push(ESC, 0x45, 0x01); // Extra bold for sale total
                commands.push(...Array.from(` Sale Total: K${total.toFixed(2)}`).map(c => c.charCodeAt(0)));
                commands.push(0x0A);
                commands.push(ESC, 0x45, 0x00);

                commands.push(...Array.from(`Payment Type: ${paymentType}`).map(c => c.charCodeAt(0)));
                commands.push(0x0A);
                commands.push(...Array.from(`Paid Amount: K${paidAmount.toFixed(2)}`).map(c => c.charCodeAt(0)));
                commands.push(0x0A);
                commands.push(...Array.from(`Change: K ${change.toFixed(2)}`).map(c => c.charCodeAt(0)));
                commands.push(0x0A);

                commands.push(ESC, 0x61, 0x00); // LEFT ALIGN
                // Units bought (sum of item quantities)
                const unitsBought = (invoiceLines || []).reduce((sum, l) => sum + Number(l.Qty || 0), 0);
                commands.push(...Array.from(`Units Bought ${unitsBought}`).map(c => c.charCodeAt(0)));
                commands.push(0x0A);

                // Cashier
                commands.push(...Array.from(`Cashier Name: ${user?.Username || 'N/A'}`).map(c => c.charCodeAt(0)));
                commands.push(0x0A);

                // Separator
                commands.push(...Array.from('-'.repeat(lineWidth)).map(c => c.charCodeAt(0)));
                commands.push(0x0A);

                // Sale reference (use actual sale number, not provisional invoice id)
                const resolvedOfflineSaleNo = (() => {
                    const candidate =
                        receipt?.saleNumber ??
                        payload?.saleNumber ??
                        payload?.SaleNum ??
                        payload?.saleNum ??
                        payload?.orderNumber ??
                        context.getters.getCurrentSale;
                    const s = candidate == null ? '' : String(candidate).trim();
                    return (!s || s === '0') ? 'N/A' : s;
                })();
                console.log(`resolvedOfflineSaleNo`,resolvedOfflineSaleNo)
                commands.push(...Array.from(`Sale No. ${resolvedOfflineSaleNo}`).map(c => c.charCodeAt(0)));
                commands.push(0x0A, 0x0A);

                // Separator
                commands.push(...Array.from('-'.repeat(lineWidth)).map(c => c.charCodeAt(0)));
                commands.push(0x0A);

                commands.push(ESC, 0x64, 0x02);// Feed 8 lines

                // Pending sync notice - Center, Bold
                commands.push(ESC, 0x61, 0x00); // Center
                commands.push(ESC, 0x45, 0x01); // Bold
                commands.push(...Array.from('*** PENDING SYNC ***').map(c => c.charCodeAt(0)));
                commands.push(0x0A);
                commands.push(...Array.from('Will sync when online').map(c => c.charCodeAt(0)));
                commands.push(0x0A);
                commands.push(ESC, 0x45, 0x00);
                commands.push(ESC, 0x61, 0x00); // Left align

                // QR Code for offline receipt verification
                // URL: https://lytningtomcat.chrilantech.com/OnlineSalesServer/invoice/{DB}/scanInvoiceQRCode/offline/{offlineInvoiceNumber}
                const qrUrl = `${window.serverApi}invoice/${store.Database}/scanInvoiceQRCode/offline/${receipt.invoice}`;

                // Center align for QR code
                commands.push(ESC, 0x61, 0x01); // Center
                commands.push(0x0A); // Line feed before QR

                // QR Code ESC/POS commands (GS (k) function for QR Code Model 2)
                // Set QR code model
                commands.push(GS, 0x28, 0x6B, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00); // Model 2

                // Set QR code size (module size: 1-8, 3 is good for receipts)
                commands.push(GS, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x43, 0x05); // Size 5

                // Set error correction level (48=L, 49=M, 50=Q, 51=H)
                commands.push(GS, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x45, 0x49); // Level M (49)

                // Store QR code data
                const qrDataLength = qrUrl.length + 3;
                const pL = qrDataLength & 0xFF;
                const pH = (qrDataLength >> 8) & 0xFF;
                commands.push(GS, 0x28, 0x6B, pL, pH, 0x31, 0x50, 0x30); // Store data
                commands.push(...Array.from(qrUrl).map(c => c.charCodeAt(0)));

                // Print QR code
                commands.push(GS, 0x28, 0x6B, 0x03, 0x00, 0x31, 0x51, 0x30); // Print stored QR code

                commands.push(0x0A); // Line feed after QR
                commands.push(ESC, 0x61, 0x00); // Left align

                // Thank you message
                commands.push(ESC, 0x61, 0x00); // Center
                commands.push(...Array.from('Thank you for using Lytsoft').map(c => c.charCodeAt(0)));
                commands.push(0x0A);

                // Software by
                commands.push(...Array.from('Software by www.chrilantech.com').map(c => c.charCodeAt(0)));
                commands.push(0x0A);
                commands.push(ESC, 0x61, 0x00); // Left align

                // Feed and cut
                commands.push(0x0A, 0x0A, 0x0A, 0x0A); // Feed lines
                commands.push(ESC, 0x64, 0x08); // Feed 8 lines
                commands.push(GS, 0x56, 0x00); // Full cut

                // Convert to base64
                const buffer = new Uint8Array(commands);
                const base64 = btoa(String.fromCharCode.apply(null, buffer));

                // Try to print via Electron/hardware bridge
                if (window?.hardwareIPC || window?.electronBridge) {
                    const result = await hardwareBridge.printBase64({
                        deviceName: settings?.Printer || 'default',
                        base64,
                        addFeed: false, // Already included in commands
                        addCut: false   // Already included in commands
                    });
                    if (result && result.ok) {
                        console.log('[Offline Print] Receipt sent successfully');
                        return true;
                    } else {
                        console.error('[Offline Print] Failed:', result?.message || 'Unknown error');
                        return false;
                    }
                }

                // Android mode
                if (window.androidMode) {
                    window.location.href = `rawbt:base64,${base64}`;
                    return true;
                }

                // Log if no printer available
                console.log('[Offline Print] No printer available. Receipt data prepared:', base64.substring(0, 100) + '...');
                return true;

            } catch (err) {
                console.error('[Offline Print] Failed to print provisional receipt:', err);
                // Don't throw - printing failure shouldn't block the sale
                return false;
            }
        },

        BtPrint: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            let settings = await Storage.getDeviceSettings();
            const pcName = await resolveHostname(settings);
            const useDocumentReceipt = settings && (settings.A4Inv || settings.A4Type === 'A5');
            if (useDocumentReceipt) {
                let storeDB = store.StoreDB;
                return openUrl(franchiseApiLytning + `PrintApi/Quotes/${storeDB}/invoice/${payload.invoice}?PC=` + pcName);
                // return openUrl(franchiseApiLytning + `PDFReport/getInvNum/${payload.invoice}/${storeDB}?PC=` + pcName);
            }
            return axios.post(printVoid + `printing/${store.StoreDB}/printReceicept/${payload.invoice}/${payload.reprint}`, settings)
                .then(({ data }) => {
                    return context.dispatch(`sendPrintToLocal`, data);
                })
                .catch(err => errorHandler.tomcatError(err));
        },

        sendPrintToLocal: async (context, base64) => {
            if (window.androidMode) {
                return window.location.href = `rawbt:base64,${base64}`
            }
            let settings = await Storage.getDeviceSettings();
            if (!settings || !settings.Printer) {
                return responses.showWarning('No printer selected')
            }
            // Electron first
            if (window?.hardwareIPC || window?.electronBridge) {
                const ok = await hardwareBridge.printBase64({ printer: settings.Printer, base64 });
                if (!ok) return responses.showWarning('Failed to send print to Electron printer');
                return true;
            }
            // Fallback to Local Hardware Service
            const obj = { printer: settings.Printer, base64 };
            return axios.post(hardwareApi + 'printing/base64', obj);
        },

        printProforma: async (context, payload) => {
            let isAllowed = await validateSecurity(`ReprintBill`);
            if (!isAllowed) return;
            let store = await Storage.getDefaultStore();
            let settings = await Storage.getDeviceSettings();
            let user = await Storage.getCurrentUser();
            if (settings && (settings.A4Inv || settings.A4Type === 'A5')) return openUrl(franchiseApi + `SaveTable/PrintQuote/${payload}/${store.StoreDB}`);
            return axios.post(printVoid + `printing/${store.StoreDB}/printProforma/${payload}/${user.Username}`, settings)
                .then(({ data }) => {
                    try {
                        return context.dispatch(`sendPrintToLocal`, data);
                    } catch (e) {
                        responses.showWarning(`Please update your Hardware Service`)
                        if (window.androidMode) return window.location.href = `rawbt:base64,${data}`;
                        return axios.post(hardwareApi + `printing/printProforma/${store.StoreDB}/${payload}/${user.Username}`, settings)
                            .catch((err) => errorHandler.tomcatError(err, false, `Cannot Print Proforma Invoice`))
                    }
                })

        },
        printBillSlip: async (context, payload) => {
            let isAllowed = await validateSecurity(`ReprintBill`);
            if (!isAllowed) return;

            const [store, settings, user] = await Promise.all([
                Storage.getDefaultStore(),
                Storage.getDeviceSettings(),
                Storage.getCurrentUser()
            ]);
            if (settings && (settings.A4Inv || settings.A4Type === 'A5')) return openUrl(franchiseApi + `SaveTable/PrintQuote/${payload}/${store.StoreDB}`);

            try {
                const response = await axios.post(printVoid + `printing/${store.StoreDB}/printBill/${payload}/${user.Username}`, settings);
                return context.dispatch('sendPrintToLocal', response.data)
            } catch (err) {
                errorHandler.tomcatError(err, false, `Bill Slip Print Error`)
            }
        },
        async getAllInvoices(context, payload) {
            let store = await Storage.getDefaultStore();
            payload = {
                ...context.getters.getGlobalDateRange,
                ...payload
            }
            return axios.post(backOfficeTomcatApi + `other/${store.StoreDB}/searchSales`, payload)
                .then(({ data }) => data)
                .catch((err) => errorHandler.tomcatError(err))
        },

        async getInvoiceLines(context, inv) {
            let store = await Storage.getDefaultStore();
            return getInvoiceByNumberThroughMainPc(store.StoreDB, inv)
                .then(({ data }) => data).catch((err) => errorHandler.tomcatError(err))
        },
        reprintLastInvoice: async (context) => {
            let isAllowed = await validateSecurity(`Reprint`);
            if (!isAllowed) return;
            $utils.showLoading()
            let latestInvoice = context.getters.getLatestInvoice;
            let invoice = latestInvoice.invoice;

            if (invoice) {
                // Check if this is an offline/provisional receipt
                if (latestInvoice.offline === true || latestInvoice.provisional === true) {
                    console.log('[Reprint] Reprinting offline receipt:', invoice);
                    // Get the original payload from outbox if available
                    try {
                        const defaultStore = await Storage.getDefaultStore();
                        const outboxItem = await outbox.getOutboxItem(
                            invoice,
                            latestInvoice.storeDB || latestInvoice.storeDb || defaultStore?.StoreDB
                        );
                        if (outboxItem) {
                            return context.dispatch('printProvisionalReceipt', {
                                receipt: latestInvoice,
                                payload: outboxItem.payload
                            }).finally(() => $utils.hideLoading());
                        }
                    } catch (err) {
                        console.error('[Reprint] Could not fetch outbox item:', err);
                    }

                    // Fallback: create basic receipt from available data
                    return context.dispatch('printProvisionalReceipt', {
                        receipt: latestInvoice,
                        payload: {
                            invoicelines: [],
                            exclTotal: 0,
                            taxTotal: 0,
                            paymentType: 'Unknown'
                        }
                    }).finally(() => $utils.hideLoading());
                }

                // Online receipt - use normal printing
                return context.dispatch('BtPrint', { invoice: invoice, reprint: true })
                    .finally(() => $utils.hideLoading());
            }

            let store = await Storage.getDefaultStore();
            return axios.get(franchiseTomcatApi + `completedsale/${store.StoreDB}/getLastInvoiceNumber`)
                .then(({ data }) => context.dispatch('BtPrint', { invoice: data, reprint: true }))
                .catch((err) => errorHandler.tomcatError(err)).finally(() => $utils.hideLoading());
        },
        /**
         * Open a saved table into the active cart.
         * Hydrates stable line id from server fields so void + UPSERT keep the same PK (ticket 22844).
         * docs: FRONTEND.md cart line identity; BE MinifiedCurrentSale.id
         */
        openTable: async (context, tab) => {
            let store = await Storage.getDefaultStore();
            let saleNum = tab.saleNumber || tab.SaleNum;

            console.log('[openTable] Opening table:', tab);
            console.log('[openTable] Table Name:', tab.Name);

            // 1) Update local state immediately so the POS shows the opened table regardless of connectivity
            try {
                // Normalize qty→Qty / unit→Unit before staging so saleLine.Qty + 1
                // never goes NaN and the next save does not persist 1
                // (qty-reset-on-save loss vector #3 — openTable path).
                // Server may emit id, Id, or lineId — normalise to item.id for void/UPSERT (22844).
                const items = normalizeSaleLines(tab.items || []).map((item, index) => ({
                    ...item,
                    index,
                    savedQty: Number(item.Qty),
                    id: item.id || item.Id || item.lineId || item.currentSaleLineId || null,
                }));

                context.commit('setCurrentSale', saleNum);
                context.commit('setCurrentLines', items);
                const customerId = tab.customer || tab.Customer;
                context.commit('setCustomer', customerId);
                if (isValidString(customerId)) {
                    await context.dispatch('getCustomerById', customerId);
                }
                context.commit('setOpenTableName', tab.Name || tab.name || null);
                context.commit('setOpenTableName', tab.Name || tab.name || null, { root: true });
                context.commit('setIsTabOpen', true);
                await context.dispatch('calculateTipAmount');
            } catch (e) {
                console.error('[Sales] Failed to stage table locally:', e);
            }

            // 2) Best-effort notify backend that this table is opened.
            // Skip remote notify when offline.
            const isOnline = connectivityMonitor.getStatus();
            const isUnsyncedLocalTable = (tab?.synced === false) || String(tab?.Name || tab?.name || '').endsWith('_offline');
            if (isOnline && !isUnsyncedLocalTable) {
                try {
                    const response = await markOpenTableThroughMainPc(store.StoreDB, saleNum);
                    if (response.status !== 200) {
                        console.warn('[Sales] Backend openTable responded with status', response.status);
                    }
                } catch (err) {
                    const msg = err?.response?.data
                        ? errorHandler.extractErrorMessage(err.response.data)
                        : (err?.message || 'Failed to notify server to open table');
                    console.warn('[Sales] openTable notify failed (non-blocking):', msg);
                }
            } else if (isUnsyncedLocalTable) {
                console.log('[Sales] Skipping backend openTable notify for unsynced local table', {
                    saleNum,
                    name: tab?.Name || tab?.name || null
                });
            }

            return true;
        },
        searchCashups: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            return axios.post(franchiseTomcatApi + `cashup/${store.StoreDB}/search`, payload)
                .then(({ data }) => data)
                .catch((err) => errorHandler.tomcatError(err))
        },

        /**
         * Post item void to Online Sales Server (or queue offline).
         * Returns `{ success, offline?, voidId?, businessReject? }` so voidItem/voidLine can gate cart removal (22844).
         * Business 4xx must not be treated as offline success — cart would desync from DB.
         * salenum==0: silent toasts — BE is best-effort audit; callers already remove locally (#26827).
         * docs: FRONTEND.md cart line identity; USER-FACING-ERRORS.md
         */
        logItemVoid: async (context, payload) => {
            let store = await Storage.getDefaultStore();
            let settings = await Storage.getDeviceSettings();
            let user = await Storage.getCurrentUser();
            // Prefer Username; fail-fast — never "System" (#26328)
            payload.User = requireSalesWriteUser(user, 'sales-module.logItemVoid');
            payload.PC = settings?.PC || await resolveHostname(settings);
            // Cart void uses getCurrentSale. All Tables void never openTable's
            // the cart, so callers must pass salenum/SaleNum on the payload
            // (TablesSaleQty). Prefer cart sale, then payload, never "undefined".
            // docs: FRONTEND.md cart line identity; ticket 22844 All Tables follow-up
            const cartSale = Number(context.getters.getCurrentSale) || 0;
            const payloadSale = Number(payload.salenum != null ? payload.salenum : payload.SaleNum) || 0;
            const saleNum = cartSale > 0 ? cartSale : payloadSale;
            payload.Reason = `Sale No. ${saleNum || 'unknown'}`;
            // Explicit salenum for BE extractVoidSaleNumber (do not rely only on Reason parse)
            if (payload.salenum == null && payload.SaleNum == null) {
                payload.salenum = saleNum;
                payload.SaleNum = saleNum;
            } else {
                // Keep both casings populated when only one was set by the caller.
                if (payload.salenum == null) payload.salenum = payload.SaleNum;
                if (payload.SaleNum == null) payload.SaleNum = payload.salenum;
            }

            // Unsaved cart: local remove is the real outcome; never surface audit noise.
            // Why: Founder #26827 — cashiers saw businessReject toast after item already left cart.
            const isUnsavedAudit = saleNum === 0;
            const fireVoidToast = (opts) => {
                if (isUnsavedAudit) return;
                Toast.fire(opts);
            };

            // Print void receipt only if enabled AND sale is saved (saleNumber > 0).
            // Use resolved saleNum so All Tables voids (payload salenum, no cart) still print.
            if (settings.PrintVoid && saleNum > 0) {
                await context.dispatch('printVoid', payload);
            }

            // Check connectivity status
            const isOnline = connectivityMonitor.getStatus();

            if (!isOnline) {
                // Offline: Queue void for later sync
                try {
                    const voidId = await voidOutbox.enqueueVoid(
                        payload,
                        store.StoreDB,
                        {
                            cashierName: user?.Username || 'Unknown',
                            cashierId: user?.UserId || null,
                            appVersion: '1.0.0'
                        }
                    );

                    if (voidId === null) {
                        console.warn('Void outbox store not available. Void cannot be queued for sync.');
                        fireVoidToast({
                            icon: 'warning',
                            title: 'Database Issue',
                            text: 'Unable to queue void for offline sync.'
                        });
                        return { success: false, offline: true };
                    }

                    // Show offline notification (saved sales only)
                    fireVoidToast({
                        icon: 'info',
                        title: 'Offline Mode',
                        text: 'Void queued for sync when online'
                    });

                    console.log(`[Void] Queued offline void: ${voidId}`);
                    return { success: true, offline: true, voidId };
                } catch (err) {
                    console.error('Failed to enqueue offline void:', err);
                    fireVoidToast({
                        icon: 'error',
                        title: 'Void Error',
                        text: 'Failed to save void operation'
                    });
                    return { success: false, offline: true };
                }
            }

            // Online: Attempt to post immediately
            return logVoidThroughMainPc(store.StoreDB, payload)
                .then(() => {
                    return { success: true, offline: false };
                })
                .catch(async (err) => {
                    const status = err?.response?.status;
                    // Business rejection (400/406/409) — fail closed; do not fake offline success (22844).
                    // businessReject lets callers avoid the misleading "Check the connection" toast (#26827).
                    // Plain-language toast for cashiers; raw server body stays in logs only (USER-FACING-ERRORS.md).
                    if (status && status >= 400 && status < 500 && status !== 408) {
                        const raw = err?.response?.data;
                        const serverMsg = typeof raw === 'string'
                            ? raw
                            : (raw?.message || raw?.error || '');
                        console.warn('[logItemVoid] business reject', status, serverMsg);
                        fireVoidToast({
                            icon: 'error',
                            title: 'Void failed',
                            text: 'This item could not be voided because the server rejected it. Refresh and try again.',
                        });
                        return { success: false, offline: false, status, businessReject: true };
                    }

                    // Network error or timeout: Fallback to offline mode
                    console.warn('Void logging failed, falling back to offline mode:', err);

                    try {
                        const voidId = await voidOutbox.enqueueVoid(
                            payload,
                            store.StoreDB,
                            {
                                cashierName: user?.Username || 'Unknown',
                                cashierId: user?.UserId || null,
                                appVersion: '1.0.0',
                                failedOnlineAttempt: true,
                                error: err.message
                            }
                        );

                        if (voidId === null) {
                            console.warn('Void outbox store not available.');
                            fireVoidToast({
                                icon: 'error',
                                title: 'Database Issue',
                                text: 'Unable to save void operation.'
                            });
                            return { success: false, offline: true };
                        }

                        fireVoidToast({
                            icon: 'warning',
                            title: 'Connection Issue',
                            text: 'Void queued and will sync automatically'
                        });

                        console.log(`[Void] Queued void after network error: ${voidId}`);
                        return { success: true, offline: true, voidId };
                    } catch (outboxErr) {
                        console.error('Failed to enqueue void after network error:', outboxErr);
                        fireVoidToast({
                            icon: 'error',
                            title: 'Void Error',
                            text: 'Failed to save void operation'
                        });
                        return { success: false, offline: true };
                    }
                });
        },
        printVoid: async (context, data) => {
            let submit;
            if (!data.items) {
                let items = [{
                    description: data.VoidDescritpiom,
                    plu: data.VoidPlu,
                    qty: data.VoidQty,
                    value: data.VoidValue
                }]

                submit = {
                    items,
                    pc: data.PC,
                    reason: data.Reason,
                    supervisor: data.SuperVisor,
                    user: data.User
                }

            } else {
                submit = data;
            }

            const isOnline = connectivityMonitor.getStatus();
            const settings = await Storage.getDeviceSettings();
            const useRoutedEndpoint = isOnline && settings.PrintVoidToItemPrinter;

            if (!isOnline) {
                // Offline: generate locally and print to main printer
                await context.dispatch('printVoidOffline', submit);
                // Also print to item printers locally if enabled
                try {
                    await context.dispatch('printVoidToItemPrinters', submit);
                } catch (err) {
                    console.warn('[Void Print] Item printer void failed (non-critical):', err);
                }
            } else if (useRoutedEndpoint) {
                // Online + routed: server returns array of {printer, base64} per printer
                try {
                    let store = await Storage.getDefaultStore();
                    const response = await axios.post(printVoid + `printing/${store.StoreDB}/printVoidReceiptRouted`, submit);
                    const results = response.data;
                    if (Array.isArray(results)) {
                        for (const result of results) {
                            if (result.printer === 'default') {
                                await context.dispatch('sendPrintToLocal', result.base64);
                            } else {
                                if (window?.hardwareIPC || window?.electronBridge) {
                                    await hardwareBridge.printBase64({ printer: result.printer, base64: result.base64, addFeed: false, addCut: false });
                                } else {
                                    await axios.post(hardwareApi + 'printing/base64', { printer: result.printer, base64: result.base64 });
                                }
                            }
                        }
                    }
                } catch (err) {
                    console.warn('[Void Print] Routed void print failed, falling back to offline mode:', err);
                    await context.dispatch('printVoidOffline', submit);
                    try {
                        await context.dispatch('printVoidToItemPrinters', submit);
                    } catch (e) {
                        console.warn('[Void Print] Item printer fallback failed:', e);
                    }
                }
            } else {
                // Online, no routing: print to main printer only
                try {
                    let store = await Storage.getDefaultStore();
                    const response = await axios.post(printVoid + `printing/${store.StoreDB}/printVoidReceipt`, submit);
                    await context.dispatch('sendPrintToLocal', response.data);
                } catch (err) {
                    console.warn('[Void Print] Server printing failed, falling back to offline mode:', err);
                    await context.dispatch('printVoidOffline', submit);
                }
            }
        },

        printVoidOffline: async (context, data) => {
            // Generate void receipt locally using the shared ESC/POS builder
            try {
                let store = await Storage.getDefaultStore();
                let settings = await Storage.getDeviceSettings();

                const base64 = buildVoidReceiptEscPos(data, data.items, store, settings);

                // Print via Electron/hardware bridge
                if (window?.hardwareIPC || window?.electronBridge) {
                    const result = await hardwareBridge.printBase64({
                        deviceName: settings?.Printer || 'default',
                        base64,
                        addFeed: false,
                        addCut: false
                    });
                    if (result && result.ok) {
                        console.log('[Void Print] Offline receipt printed successfully');
                        return true;
                    } else {
                        console.error('[Void Print] Failed:', result?.message || 'Unknown error');
                        return false;
                    }
                }

                // Android mode
                if (window.androidMode) {
                    window.location.href = `rawbt:base64,${base64}`;
                    return true;
                }

                console.log('[Void Print] No printer available');
                return false;

            } catch (err) {
                console.error('[Void Print] Failed to print offline void receipt:', err);
                return false;
            }
        },

        printVoidToItemPrinters: async (context, data) => {
            // Send void receipt to each voided item's assigned kitchen/bar printer
            try {
                if (window.androidMode) return;

                const settings = await Storage.getDeviceSettings();
                if (!settings || !settings.PrintVoidToItemPrinter) return;

                const items = data.items || [];
                if (items.length === 0) return;

                // Group items by their assigned printer
                const printerGroups = {};

                for (const item of items) {
                    const plu = item.plu;
                    console.log(`[ItemPrinter] Item: ${item.description}, PLU: ${plu}`);

                    if (!plu) {
                        console.log(`[ItemPrinter]   → SKIP: No PLU`);
                        continue;
                    }

                    let stockSetting = null;
                    try {
                        stockSetting = await Storage.getStockSetting(plu);
                    } catch (_) {
                        console.log(`[ItemPrinter]   → SKIP: No stock setting found`);
                        continue;
                    }

                    const printerId = stockSetting?.printer || stockSetting?.Printer;
                    console.log(`[ItemPrinter]   → stockSetting.printer: ${stockSetting?.printer}, stockSetting.Printer: ${stockSetting?.Printer}, printerId: ${printerId}`);

                    if (!printerId || String(printerId) === '0' || String(printerId) === '1000') {
                        console.log(`[ItemPrinter]   → SKIP: printerId is ${printerId} (no printer/0/1000)`);
                        continue;
                    }

                    const printerName = settings[`Printer${printerId}`];
                    console.log(`[ItemPrinter]   → Printer${printerId} = "${printerName}"`);

                    if (!printerName) {
                        console.log(`[ItemPrinter]   → SKIP: No printer name configured`);
                        continue;
                    }

                    console.log(`[ItemPrinter]   → ADDING to group for printer: ${printerName}`);
                    const key = String(printerId);
                    if (!printerGroups[key]) {
                        printerGroups[key] = { printerName, items: [] };
                    }
                    printerGroups[key].items.push(item);
                }

                const groupKeys = Object.keys(printerGroups);
                console.log(`[ItemPrinter] Final groups: ${groupKeys.length}`, printerGroups);

                if (groupKeys.length === 0) {
                    console.log(`[ItemPrinter] No items to print to item printers`);
                    return;
                }

                const store = await Storage.getDefaultStore();

                for (const key of groupKeys) {
                    const group = printerGroups[key];
                    console.log(`[ItemPrinter] PRINTING to "${group.printerName}" - ${group.items.length} item(s)`);
                    const base64 = buildVoidReceiptEscPos(data, group.items, store, settings);

                    if (window?.hardwareIPC || window?.electronBridge) {
                        await hardwareBridge.printBase64({
                            printer: group.printerName,
                            base64,
                            addFeed: false,
                            addCut: false
                        });
                    } else {
                        await axios.post(hardwareApi + 'printing/base64', {
                            printer: group.printerName,
                            base64
                        });
                    }
                }
            } catch (err) {
                console.error('[Void Print] Failed to print void to item printers:', err);
            }
        },

        openCashDrawer: async (context) => {
            try {
                // Skip for Android mode
                let isAndroid = window.androidMode;
                if (isAndroid) {
                    return { ok: false, error: 'Not supported on Android' };
                }

                // Get settings for printer and cash drawer configuration
                let settings = await Storage.getDeviceSettings();

                // Use Electron IPC if available
                if (window?.hardwareIPC || window?.electronBridge) {
                    // CashDrawerKick setting contains the PRINTER NAME that controls the drawer
                    // The actual kick code is handled by main.js using standard ESC/POS commands
                    const payload = {
                        printerName: (settings?.CashDrawerKick || '').trim() || (settings?.Printer || '').trim()
                    };

                    const result = await hardwareBridge.openCashDrawer(payload);

                    if (result) {
                        return { ok: true };
                    } else {
                        console.error('[Cash Drawer] Failed to open via Electron');
                        return { ok: false, error: 'Failed to open cash drawer' };
                    }
                }

                // Fallback: this shouldn't be reached in Electron mode, but keeping for safety
                console.warn('[Cash Drawer] No Electron IPC available');
                return { ok: false, error: 'Hardware bridge not available' };

            } catch (err) {
                console.error('[Cash Drawer] Exception:', err);
                return { ok: false, error: String(err?.message || err) };
            }
        },

        getAllActiveSpecials: async (context) => {
            let store = await Storage.getDefaultStore()
            if (!store) return;
            let DB = store.StoreDB;
            return Promise.all([
                axios.get(serverApi + `specials/${DB}/getActiveSpecials?type=discount`)
                    .then(({ data }) => context.commit(`setDiscountSpecials`, data)),

                axios.get(serverApi + `specials/${DB}/getActiveSpecials?type=combo`)
                    .then(({ data }) => context.commit(`setComboSpecials`, data)),

                axios.get(serverApi + `specials/${DB}/getActiveSpecials?type=multi`)
                    .then(({ data }) => context.commit(`setMultiSpecials`, data)),
            ])
        },

        retrySmartInvoice: async (context, invoiceNumber) => {
            let store = await Storage.getDefaultStore()
            return axios.get(backOfficeTomcatApi + `other/${store.StoreDB}/sendSmartInvoice?invoice=` + invoiceNumber)
                .then(({ data }) => data)
                .catch(err => {
                    // Handle NullPointerException for missing refund number
                    if (err.response &&
                        err.response.status === 500 &&
                        err.response.data &&
                        err.response.data.response &&
                        err.response.data.response.type &&
                        err.response.data.response.type.includes("NullPointerException") &&
                        err.response.data.response.type.includes("getRefundNumber()")) {
                        return Swal.fire({
                            title: 'Smart Invoice Error',
                            icon: 'warning',
                            html: `<p>Unable to process smart invoice due to missing refund number.</p>
                                   <p>Please contact support: +260958168829</p>`,
                            showCloseButton: true,
                        });
                    }

                    const sanitizeErrorMessage = (message) => {
                        if (!message) return 'An error occurred';
                        return message.replace(/[<>]/g, match => match === '<' ? '&lt;' : '&gt;');
                    };

                    if (err.response && err.response.data && err.response.data.response) {
                        const rawErrorMessage = err.response.data.response.Message || err.response.data.response.message || 'An error occurred';
                        const errorMessage = sanitizeErrorMessage(rawErrorMessage);
                        const errorCode = err.response.data.response.Code || err.response.data.code || '';

                        return Swal.fire({
                            title: 'Smart Invoice Error',
                            icon: 'warning',
                            html: `${errorCode ? `<p>ZRA failure Code: ${errorCode}</p>` : ''}
                                   <p>${errorMessage}</p>
                                   <p>Please contact support: +260958168829</p>`,
                            showCloseButton: true,
                        });
                    }
                    return errorHandler.tomcatError(err);
                });
        },

    }
}

